/**
 * EditVisitorNext - Add/Edit Visitor form.
 * JUSTO-64: Lead source shows Channel Partner & Referral Partner only after LFSM + SM (same cascade as the app);
 * those two options and CP lead type (Walk-in / Tele-calling) are rendered as radio buttons.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import strings from "translate/language";
import {
  addVisitor,
  addVisitProperty,
  clearVisitorDetailContext,
  editVisitor,
  removeVisitorMobileCheck,
  visitorFormUpdate,
  visitorMobileCheck,
  visitorDetail,
  checkReferenceNmbrExist,
} from "reduxStore/action/visitorActions";
import {
  mobileNumberValid,
  pancardValidation,
  seo,
  validateAadhaar,
  nameValid,
  handleDetailResponse,
  isEmpty,
  hideMobileNumber,
  emailAddressValid,
  handleListResponse,
  isValidMongoObjectId,
  filterAlphabetsOnlyName,
  visitorNameInputHasInvalidChars,
  normalizeMongoId,
} from "utils/utils";
import {
  ensureMdocClientConfigHydrated,
  isMdocEmailRequired,
} from "utils/mdocProperty";
import Breadcrumb from "component/common/breadcrumb";
import { addFollowUp } from "reduxStore/action/followUpActions";
import { AutocompletePlace } from "component/common/PlacesAutocomplete";
import moment from "moment";
import { universalRemoveAction } from "../../reduxStore/action/authActions";
import { VISITOR_MOBILE_CHECK } from "../../reduxStore/types";
import {
  getConfiguration,
  getMasterList,
  getEthnicity,
} from "reduxStore/action/masterActions";
import {
  SourcingManager,
  SourcingTL,
  scm_id,
  sourcing_head_id,
  ClosingManager,
  ClosingTL,
  closing_head_id,
  SiteHead,
  ClusterHead,
  BusinessHead,
  admin_id,
  AdminRoleId,
  SourcingHead,
  referenceMaster,
  channelPartnerMaster,
  directWalkinMaster,
  exhibitionMaster,
  referralPartnerMaster,
  GRE,
  isGreRoleId,
  CSTL,
  isCstlRoleId,
  ANNUAL_HOUSEHOLD_INCOME_OPTIONS,
  SCM,
  nextDaycallId,
} from "utils/constants";
import { CpType, CONST_IDS } from "component/utilities/DemoData";
import { getAssignCPList } from "reduxStore/action/channelPartnerAction";
import { getEmployeeList } from "reduxStore/action/companyActions";
import { getAgentProperty } from "reduxStore/action/propertyActions";
import { apiCall, errorToast, successToast } from "utils/httpClient";
import apiEndPoints from "utils/apiEndPoints";
import { getUserDetailForVisitor } from "reduxStore/action/usersActions";
import { getPropertyDetail } from "reduxStore/action/propertyActions";
import usePermission from "utils/usePermission";
import PropertyDeveloperBanner from "component/common/PropertyDeveloperBanner";
import AllocateSM from "component/common/AllocateCM";
import MobileOtpVerification from "component/common/MobileOtpVerification";
import { preloadGreBannerImage } from "utils/greBannerImageLoader";
import { findPropertyInPools } from "utils/grePropertyBanner";
import {
  initialVisitorMobileOtpState,
  isGreVisitorOtpRequired,
  isGreMobileCurrentlyVerified,
  isGreMobileVerificationSatisfied,
  resolveGrePropertyChangeOtpState,
  isPropertyGreMobileOtpEnabled,
  isVisitorMobileValid,
  resolveGreDefaultProperty,
  resolveGreMobileOtpEnabled,
  shouldShowGreSendOtp,
  resendVisitorMobileOtp,
  resolveVisitorOtpFailureMessage,
  resolveVisitorOtpSentMessage,
  isVisitorOtpSendSuccess,
  sendVisitorMobileOtp,
  verifyVisitorMobileOtp,
} from "utils/visitorMobileOtp";
import "./EditVisitorNextGre.css";
import "./VisitorListing.css";
import "../Appointment/AppointmentListing.css";
import AppointmentScheduleFields from "../Appointment/AppointmentScheduleFields";
import {
  GreVisitorFormSectionHeader,
  GreVisitorFormStepBar,
} from "./GreVisitorFormUi";

const VISITOR_VALIDATION_FIELD_LABELS = {
  first_name: strings.visitorName,
  mobile: strings.mobileNumber,
  lead_source: strings.leadSource,
  cp_lead_type: "Channel Partner Lead Type",
  cp_id: "Channel Partner",
  referrer_contact: "Referrer Mobile",
  referrer_name: "Referrer Name",
  referrer_email: "Referrer Email",
  property_id: strings.propertyName,
  gender: strings.gender,
  email: strings.email,
  whatsapp_no: strings.whatsappNo,
  visit_confirmation_status: "Mobile Verification",
  walkin_remark: strings.walkinRemark,
  followup_date: strings.addVisitorFollowupDate,
  followup_time: strings.addVisitorFollowupTime,
};

const showMissingRequiredFieldsToast = (err = {}) => {
  const labels = [
    ...new Set(
      Object.keys(err).map(
        (key) =>
          VISITOR_VALIDATION_FIELD_LABELS[key] ||
          key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase()),
      ),
    ),
  ];

  if (!labels.length) {
    return;
  }

  errorToast(`${strings.missingRequiredFieldsPrefix} ${labels.join(", ")}`, {
    autoClose: Math.min(10000, 4000 + labels.length * 700),
  });
};

/**
 * LFSM UI / validation mode = checkbox (or existing for_sm_id on edit).
 * Do NOT treat CP/RP lead source alone as LFSM — that flipped SCM/SM into the
 * property→SM→lead-source cascade when they pick RP, then bounced back when SM
 * cleared lead_source.
 */
const resolveLfsmLead = (sourceForm = {}) =>
  Boolean(sourceForm.isSousingManagerLead) ||
  Boolean(sourceForm.for_sm_id);

/** LFSM: always keep for_sm_id on submit; drop it only when LFSM is off. */
const prepareVisitorSubmitPayload = (sourceForm) => {
  const formData = { ...sourceForm };
  if (formData?.lead_source === referralPartnerMaster) {
    formData.lead_source = referenceMaster;
    if (
      formData.referrel_partner === undefined ||
      formData.referrel_partner === null ||
      formData.referrel_partner === ""
    ) {
      formData.referrel_partner = 1;
    }
  }
  if (resolveLfsmLead(formData)) {
    formData.isSousingManagerLead = true;
    formData.for_sm_id = sourceForm.for_sm_id || "";
  } else {
    delete formData.for_sm_id;
  }
  return formData;
};

const EditVisitorNext = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [form, setForm] = useState({});
  const [formVaild, setFormVaild] = useState(null);
  const [visitResponse, setVisitResponse] = useState({});
  const [visitModule, setVisitModule] = useState(false);
  const [leadSourceType, setleadSourceType] = useState([]);
  const [allLeadSource, setAllLeadSource] = useState([]);
  const [agentList, setAgentList] = useState([]);
  const [viewDetails, setViewDetails] = useState({});
  const [dropdownAgentList, setDropdownAgentList] = useState([]);
  const [companyList, setCompanyList] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);
  const [allProperty, setAllProperty] = useState([]);
  const [sourcingPropertyList, setSourcingPropertyList] = useState([]);
  const [emptyArray, setEmptyArray] = useState([]);
  const [smData, setSmData] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [selectedSMRoleId, setSelectedSMRoleId] = useState(null);
  const [ethnicityList, setEthnicityList] = useState([]);
  const [mobileOtpState, setMobileOtpState] = useState(
    initialVisitorMobileOtpState,
  );
  const [configurationsType, setConfigurationsType] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAllocateModal, setShowAllocateModal] = useState(false);
  const [createdLeadId, setCreatedLeadId] = useState(null);
  const [createdPropertyId, setCreatedPropertyId] = useState(null);
  const [createAndSchedule, setCreateAndSchedule] = useState(false);
  const processedResponseRef = useRef(null);
  const processedEditResponseRef = useRef(null);
  const editSubmitPendingRef = useRef(false);
  const createSubmitPendingRef = useRef(false);
  const pendingFollowUpRef = useRef(null);
  const grePropertyAutoSelectedRef = useRef(false);
  const greEditMobileSeededRef = useRef(false);

  const location = useLocation();
  const [leadId, setLeadId] = useState(null);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const leadIdFromQuery = queryParams.get("lead_id");
    setLeadId(leadIdFromQuery);

    // Add Visitor (no lead_id): always start blank. Persisted redux still holds the
    // last visitorDetail / form draft across hard reload and would refill the fields.
    if (!leadIdFromQuery) {
      setForm({});
      setMobileOtpState(initialVisitorMobileOtpState);
      setSmData([]);
      setSelectedSMRoleId(null);
      setShowAllocateModal(false);
      setCreatedLeadId(null);
      setCreatedPropertyId(null);
      grePropertyAutoSelectedRef.current = false;
      greEditMobileSeededRef.current = false;
      processedResponseRef.current = null;
      createSubmitPendingRef.current = false;
      dispatch(visitorFormUpdate(null));
      dispatch(clearVisitorDetailContext());
    }
  }, [location.search, dispatch]);


  const navigatpage = async (navname) => {
    navigate(navname);
  };

  const leadSourceSelector = useSelector((state) => state.masterData);
  const visitorDataSelector = useSelector((state) => state.visitorForm);
  const employeeData = useSelector((state) => state.companydata) || {};
  const cpUserList = useSelector((state) => state.channelPartner) || {};
  const { response: res } = useSelector((state) => state.login);
  const visitorDetailSelector = useSelector((state) => state.visitor);
  const userData = useSelector((state) => state.userData);
  const userDetails = userData?.userDetails?.data;

  const propertyData = useSelector((state) => state.propertyData);

  const getPropertyDetailSelector = useSelector((state) => state.propertyData);
  const configurationsSelector = useSelector((state) => state.configurations);
  const ethnicitySelector = useSelector((state) => state.ethnicitys);
  const { edit } = usePermission({ edit: "edit_lead_source" });

  useEffect(() => {
    if (!form.property_id) {
      return undefined;
    }

    const propertyId = normalizeMongoId(form.property_id);
    if (!isValidMongoObjectId(propertyId)) {
      return undefined;
    }

    let cancelled = false;

    // Wait for the private banner blob first — CloudFront gre_banner_url is 403,
    // and starting getPropertyDetail in parallel starves the banner + OTP APIs.
    const match = findPropertyInPools(propertyId, [
      sourcingPropertyList,
      allProperty,
    ]);
    const hasBanner = match ? Boolean(match.gre_banner) : null;

    (async () => {
      if (hasBanner !== false) {
        await preloadGreBannerImage(propertyId, { hasBanner });
      }
      if (cancelled) {
        return;
      }
      dispatch(
        getPropertyDetail({
          property_id: propertyId,
          suppressGlobalLoader: true,
        }),
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch, form.property_id]);

  useEffect(() => {
    if (getPropertyDetailSelector.detail) {
      const { response } = getPropertyDetailSelector;
      if (response?.status === 200) {
        setViewDetails(handleDetailResponse(response?.data));
      } else {
        setViewDetails({});
      }
    }
  }, [getPropertyDetailSelector]);

  useEffect(() => {
    if (res?.data?._id && !userData?.userDetails) {
      // Make sure the user data is not already available
      dispatch(getUserDetailForVisitor({ user_id: res?.data?._id }));
    }
  }, [dispatch, res?.data?._id, userData?.userDetails]); // Depend on userId and userData

  const visitorMobileCheckNumber = useSelector(
    (state) => state.visitorMobileCheck,
  );

  useEffect(() => {
    // Multi-step draft restore only for edit / step flows — never for Add Visitor.
    if (!leadId) {
      return;
    }
    if (visitorDataSelector.update && visitorDataSelector.response) {
      setForm({ ...visitorDataSelector.response });
    }
  }, [visitorDataSelector, leadId]);

  useEffect(() => {
    if (leadId) {
      dispatch(visitorDetail({ lead_id: leadId }));
    }
  }, [dispatch, leadId]);

  let role_id;
  let user_id;

  const isCreated = useSelector((state) => {
    const { response } = state.login;
    user_id = response?.data?.user_id;
    role_id = response?.data.role_id;
    return form?.create_by === handleDetailResponse(response.data)._id
      ? true
      : false;
  });

  // JUSTO-64: GRE treated as CM team for lead-source / "Lead for SM" behaviour (allocates to CM/SCM only).
  // CSTL has both closing TL and sourcing TL rights — same LFSM checkbox as the apps.
  const Cmteam =
    ClosingTL === role_id ||
    isCstlRoleId(role_id) ||
    ClosingManager === role_id ||
    closing_head_id === role_id ||
    isGreRoleId(role_id) ||
    scm_id === role_id;

  const SMteam =
    SourcingTL === role_id ||
    SourcingManager === role_id ||
    sourcing_head_id === role_id;

  const mixTeam =
    scm_id === role_id ||
    SiteHead === role_id ||
    ClusterHead === role_id ||
    BusinessHead === role_id ||
    admin_id === role_id;

  const isGREUser = isGreRoleId(res?.data?.role_id || role_id);
  // Super admin = suadminrole (AdminRoleId) or justo_admin (admin_id).
  const isSuperAdmin =
    String(role_id || "") === String(AdminRoleId) ||
    String(role_id || "") === String(admin_id) ||
    String(res?.data?.role_id || "") === String(AdminRoleId) ||
    String(res?.data?.role_id || "") === String(admin_id);
  const isSinglePageAdd = !leadId;
  const isSinglePageEdit = !!leadId;
  // GRE keep their dedicated add chrome; all other roles use one-page JUSTO form.
  const hideGreLeadStatusAndPriority = isGREUser && isSinglePageAdd;
  const isGreSinglePageAdd = hideGreLeadStatusAndPriority;
  const isJustoSinglePage = !isGreSinglePageAdd;
  const isSinglePageForm = true;
  const showWalkinRemarkSection =
    isSinglePageAdd &&
    (role_id === ClosingManager || role_id === SCM || role_id === scm_id);
  const isClosingTeamOnly =
    ClosingTL === role_id ||
    ClosingManager === role_id ||
    closing_head_id === role_id;
  // GRE users see per-property banner on visitor form when one is configured.
  const showGreFormBanner = isGREUser;
  const greDefaultProperty = useMemo(
    () =>
      isGreSinglePageAdd
        ? resolveGreDefaultProperty([sourcingPropertyList])
        : null,
    [isGreSinglePageAdd, sourcingPropertyList],
  );
  const propertyGreOtpEnabled = useMemo(() => {
    if (
      viewDetails?.gre_mobile_otp_enabled !== undefined &&
      isValidMongoObjectId(form?.property_id)
    ) {
      return isPropertyGreMobileOtpEnabled(viewDetails);
    }
    const propertyId =
      form?.property_id || greDefaultProperty?.property_id || "";
    return resolveGreMobileOtpEnabled(propertyId, [
      sourcingPropertyList,
      allProperty,
    ]);
  }, [
    viewDetails,
    form?.property_id,
    greDefaultProperty,
    sourcingPropertyList,
    allProperty,
  ]);
  const greOtpPropertyId =
    form?.property_id || greDefaultProperty?.property_id || "";
  const greOtpPropertyTitle =
    form?.property_title || greDefaultProperty?.property_title || "";
  const requireGreMobileOtp = isGreVisitorOtpRequired(
    isGREUser,
    propertyGreOtpEnabled,
  );
  const previousRequireGreMobileOtpRef = useRef(requireGreMobileOtp);

  useEffect(() => {
    greEditMobileSeededRef.current = false;
  }, [leadId]);

  // Create: when OTP becomes required, always clear synthetic verification.
  useEffect(() => {
    const wasRequired = previousRequireGreMobileOtpRef.current;
    previousRequireGreMobileOtpRef.current = requireGreMobileOtp;

    if (leadId || !isGREUser || wasRequired === requireGreMobileOtp) {
      return;
    }

    if (!requireGreMobileOtp) {
      setMobileOtpState({ ...initialVisitorMobileOtpState });
      setForm((prev) => ({
        ...prev,
        visit_confirmation_status: 1,
      }));
      return;
    }

    setMobileOtpState({ ...initialVisitorMobileOtpState });
    setForm((prev) => ({
      ...prev,
      visit_confirmation_status: "",
    }));
  }, [requireGreMobileOtp, isGREUser, leadId]);

  useEffect(() => {
    if (!leadId || !requireGreMobileOtp || greEditMobileSeededRef.current) {
      return;
    }

    const mobile = String(form?.mobile || "").trim();
    if (!isVisitorMobileValid(mobile)) {
      return;
    }

    greEditMobileSeededRef.current = true;
    setMobileOtpState({
      mobileVerified: true,
      verifiedMobile: mobile,
      otpSent: false,
      otp: "",
      sendingOtp: false,
      verifyingOtp: false,
    });
    setForm((prev) => ({
      ...prev,
      visit_confirmation_status: 1,
    }));
  }, [leadId, requireGreMobileOtp, form?.mobile]);

  useEffect(() => {
    if (isSinglePageForm) {
      dispatch(getConfiguration());
    }
  }, [dispatch, isSinglePageForm]);

  useEffect(() => {
    if (!isSinglePageForm) {
      return;
    }
    if (configurationsSelector.response) {
      const { response } = configurationsSelector;
      if (response?.status === 200) {
        if (viewDetails && viewDetails.configuration_area) {
          const configurationIds = viewDetails.configuration_area.map(
            (item) => item.configuration_id,
          );
          const filteredConfigurations = response.data.filter((config) =>
            configurationIds.includes(config._id),
          );
          setConfigurationsType(filteredConfigurations);
        } else {
          setConfigurationsType([]);
        }
      } else {
        setConfigurationsType([]);
      }
    }
  }, [configurationsSelector, viewDetails, isSinglePageForm]);

  useEffect(() => {
    if (!isGreSinglePageAdd || !createSubmitPendingRef.current) {
      return;
    }
    const { create, response } = visitorDetailSelector;
    const responseId = response?.data?._id;
    const responseStatus = response?.status;

    if (create && response && responseStatus === 200 && responseId) {
      if (processedResponseRef.current === responseId) {
        return;
      }
      processedResponseRef.current = responseId;
      createSubmitPendingRef.current = false;

      const leadIdFromResponse = response?.data?._id;
      const propertyId = response?.data?.property_id;

      if (leadIdFromResponse && propertyId) {
        setCreatedLeadId(leadIdFromResponse);
        setCreatedPropertyId(propertyId);
        setShowAllocateModal(true);
        dispatch(visitorFormUpdate(null));
      }
    }
  }, [
    dispatch,
    isGreSinglePageAdd,
    visitorDetailSelector.create,
    visitorDetailSelector.response?.status,
    visitorDetailSelector.response?.data?._id,
  ]);

  // Non-GRE one-page create: follow-up (CM/SCM) + navigate
  useEffect(() => {
    if (!isSinglePageAdd || isGreSinglePageAdd || !createSubmitPendingRef.current) {
      return;
    }

    const { create, response } = visitorDetailSelector;
    const responseId = response?.data?._id;
    const responseStatus = response?.status;

    if (!create || !response || responseStatus !== 200 || !responseId) {
      return;
    }

    if (processedResponseRef.current === responseId) {
      return;
    }
    processedResponseRef.current = responseId;
    createSubmitPendingRef.current = false;

    const pending = pendingFollowUpRef.current;
    const isCMorSCM =
      role_id === ClosingManager || role_id === SCM || role_id === scm_id;
    if (
      isCMorSCM &&
      pending &&
      pending.followup_date &&
      pending.followup_time
    ) {
      const followLeadId =
        response?.data?._id ||
        response?.data?.lead_id ||
        response?.data?.visitor_id;
      const appointmentDetail = response?.appointmentDetail;
      const appointmentId =
        appointmentDetail?._id || appointmentDetail?.appointment_id;
      const followUpPayload = {
        lead_id: followLeadId,
        followup_status: nextDaycallId,
        next_followup_date: pending.followup_date,
        followup_time: pending.followup_time,
        remark: "",
        walkin_remark: pending.walkin_remark || "",
      };
      if (appointmentId) {
        followUpPayload.appointment_id = appointmentId;
        followUpPayload.update_type = 10;
      }
      dispatch(addFollowUp(followUpPayload));
      pendingFollowUpRef.current = null;
    }

    setForm((prev) => ({
      ...prev,
      walkin_remark: "",
      followup_date: "",
      followup_time: "",
    }));
    dispatch(visitorFormUpdate(null));

    if (createAndSchedule) {
      const scheduleLeadId = response?.data?._id;
      const propertyId = response?.data?.property_id;
      navigatpage(
        `/add_appointment?lead_id=${scheduleLeadId}&property_id=${propertyId}`,
      );
      setCreateAndSchedule(false);
      return;
    }

    const appointment_id = localStorage.getItem("appointment_id");
    if (response?.appointmentDetail?.appointment_id) {
      if (Cmteam && !isGREUser) {
        navigatpage(
          `/viewappointment/${response?.appointmentDetail?.appointment_id}`,
        );
      } else {
        navigatpage("/visitor");
      }
    } else if (appointment_id) {
      navigatpage(`/viewappointment/${appointment_id}`);
      setTimeout(() => {
        localStorage.removeItem("appointment_id");
      }, 5000);
    } else {
      navigatpage("/visitor");
    }
  }, [
    Cmteam,
    createAndSchedule,
    dispatch,
    isGreSinglePageAdd,
    isGREUser,
    isSinglePageAdd,
    role_id,
    visitorDetailSelector.create,
    visitorDetailSelector.response,
  ]);

  useEffect(() => {
    if (!isSinglePageEdit || !editSubmitPendingRef.current) {
      return;
    }

    const { create, response } = visitorDetailSelector;
    const responseId = response?.data?._id || leadId;
    const responseStatus = response?.status;

    if (!create || !response || responseStatus !== 200 || !responseId) {
      return;
    }

    if (processedEditResponseRef.current === responseId) {
      return;
    }

    processedEditResponseRef.current = responseId;
    editSubmitPendingRef.current = false;
    dispatch(visitorFormUpdate(null));
    navigatpage("/visitor");
  }, [
    dispatch,
    isSinglePageEdit,
    leadId,
    visitorDetailSelector.create,
    visitorDetailSelector.response?.status,
    visitorDetailSelector.response?.data?._id,
  ]);

  const handleSendVisitorOtp = async () => {
    if (!isVisitorMobileValid(form?.mobile)) {
      errorToast(strings.mobileValidErr);
      return;
    }

    setMobileOtpState((prev) => ({
      ...prev,
      sendingOtp: true,
      otpSendError: "",
    }));
    try {
      const res = await sendVisitorMobileOtp(
        form?.mobile,
        "+91",
        greOtpPropertyTitle,
        greOtpPropertyId,
      );
      if (isVisitorOtpSendSuccess(res?.data)) {
        setMobileOtpState((prev) => ({
          ...prev,
          sendingOtp: false,
          otpSent: true,
          otp: "",
          otpSendError: "",
        }));
        successToast(
          resolveVisitorOtpSentMessage(res?.data, strings.otpSentSuccess),
        );
      } else {
        const message = resolveVisitorOtpFailureMessage(
          res?.data,
          strings.otpSendFailed,
        );
        setMobileOtpState((prev) => ({
          ...prev,
          sendingOtp: false,
          otpSendError: message,
        }));
        errorToast(message);
      }
    } catch (_error) {
      setMobileOtpState((prev) => ({
        ...prev,
        sendingOtp: false,
        otpSendError: strings.otpSendFailed,
      }));
      errorToast(strings.otpSendFailed);
    }
  };

  const handleVerifyVisitorOtp = async () => {
    if (mobileOtpState.otp.length !== 4) {
      errorToast(strings.otpEnterFourDigits);
      return;
    }

    setMobileOtpState((prev) => ({ ...prev, verifyingOtp: true }));
    try {
      const res = await verifyVisitorMobileOtp(
        form?.mobile,
        "+91",
        mobileOtpState.otp,
        greOtpPropertyId,
      );
      if (res?.data?.status === 200) {
        setMobileOtpState((prev) => ({
          ...prev,
          verifyingOtp: false,
          mobileVerified: true,
          verifiedMobile: String(form?.mobile || "").trim(),
          otpSent: false,
          otp: "",
        }));
        setForm((prevForm) => ({
          ...prevForm,
          visit_confirmation_status: 1,
        }));
        successToast(res?.data?.message || strings.otpVerifySuccess);
      } else {
        setMobileOtpState((prev) => ({ ...prev, verifyingOtp: false }));
        errorToast(res?.data?.message || strings.otpInvalid);
      }
    } catch (_error) {
      setMobileOtpState((prev) => ({ ...prev, verifyingOtp: false }));
      errorToast(strings.otpVerifyFailed);
    }
  };

  const handleResendVisitorOtp = async () => {
    setMobileOtpState((prev) => ({
      ...prev,
      sendingOtp: true,
      otp: "",
      otpSendError: "",
    }));
    try {
      const res = await resendVisitorMobileOtp(
        form?.mobile,
        "+91",
        greOtpPropertyTitle,
        greOtpPropertyId,
      );
      if (isVisitorOtpSendSuccess(res?.data)) {
        setMobileOtpState((prev) => ({
          ...prev,
          sendingOtp: false,
          otpSent: true,
          otpSendError: "",
        }));
        successToast(
          resolveVisitorOtpSentMessage(res?.data, strings.otpResentSuccess),
        );
      } else {
        const message = resolveVisitorOtpFailureMessage(
          res?.data,
          strings.otpSendFailed,
        );
        setMobileOtpState((prev) => ({
          ...prev,
          sendingOtp: false,
          otpSendError: message,
        }));
        errorToast(message);
      }
    } catch (_error) {
      setMobileOtpState((prev) => ({
        ...prev,
        sendingOtp: false,
        otpSendError: strings.otpSendFailed,
      }));
      errorToast(strings.otpSendFailed);
    }
  };

  const handleVisitorOtpChange = (otp) => {
    setMobileOtpState((prev) => ({ ...prev, otp }));
  };

  const handleGreMobileInput = (event) => {
    const digits = event.target.value.replace(/\D/g, "").slice(0, 10);

    setMobileOtpState((prev) => {
      const matchesVerified =
        prev.verifiedMobile && digits === prev.verifiedMobile;

      setForm((prevForm) => ({
        ...prevForm,
        mobile: digits,
        visit_confirmation_status: matchesVerified ? 1 : "",
      }));

      if (matchesVerified) {
        return {
          ...initialVisitorMobileOtpState,
          verifiedMobile: prev.verifiedMobile,
          mobileVerified: true,
        };
      }

      return {
        ...initialVisitorMobileOtpState,
        verifiedMobile: prev.verifiedMobile,
      };
    });
  };

  const isGreMobileVerified = requireGreMobileOtp
    ? isGreMobileCurrentlyVerified(form?.mobile, mobileOtpState)
    : isGreMobileVerificationSatisfied(
        form?.mobile,
        mobileOtpState,
        form?.visit_confirmation_status,
      );
  const showGreSendOtp =
    !isGreMobileVerified &&
    shouldShowGreSendOtp(form?.mobile, mobileOtpState);
  const isLfsmLead = resolveLfsmLead(form);
  const showLeadForSmOnEdit = Boolean(leadId && isLfsmLead);
  // On add, CM/CTL/CSTL/GRE/SCM can toggle LFSM; on edit show read-only when LFSM applied.
  const showLeadForSmCheckbox = Cmteam && !leadId;
  const showInlineVerifyButton =
    requireGreMobileOtp && !mobileOtpState.otpSent && !isGreMobileVerified;
  const showLeadForSmBesideMobile =
    showLeadForSmCheckbox && !requireGreMobileOtp;
  const showLeadForSmSeparateRow =
    showLeadForSmCheckbox && requireGreMobileOtp;
  const useGreMobileSplitLayout =
    showInlineVerifyButton || showLeadForSmBesideMobile;

  const handleResetGreMobileVerification = () => {
    const matchesVerified =
      mobileOtpState.verifiedMobile &&
      form?.mobile === mobileOtpState.verifiedMobile;

    setMobileOtpState({
      ...initialVisitorMobileOtpState,
      verifiedMobile: mobileOtpState.verifiedMobile,
      mobileVerified: Boolean(matchesVerified),
    });
    setForm((prevForm) => ({
      ...prevForm,
      visit_confirmation_status: matchesVerified ? 1 : "",
    }));
  };

  // const leadsourcefilteredData = leadSourceSelector?.response?.data.filter(
  //   (obj) =>
  //     Cmteam
  //       ? obj.title !== "Channel Partner" && obj._id !== referralPartnerMaster
  //       : SMteam
  //       ? obj.title === "Channel Partner"
  //       : obj.title !== ""
  // );

  useEffect(() => {
    if (visitorMobileCheckNumber?.response?.status) {
      const checkStatus = visitorMobileCheckNumber.response.status;
      dispatch(removeVisitorMobileCheck());
      if (requireGreMobileOtp) {
        return;
      }
      if (checkStatus === 200) {
        setForm((prevForm) => ({
          ...prevForm,
          visit_confirmation_status: 1,
        }));
      } else if (checkStatus === 201 || checkStatus === 202) {
        setVisitModule(true);
        setVisitResponse(visitorMobileCheckNumber.response);
        dispatch(universalRemoveAction(VISITOR_MOBILE_CHECK));
      }
    }
  }, [dispatch, visitorMobileCheckNumber, requireGreMobileOtp]);

  useEffect(() => {
    if (visitorMobileCheckNumber?.response1?.status === 200) {
      // Update the form with the referrer name and email from the API response
      setForm((prevForm) => ({
        ...prevForm,
        referrer_name: visitorMobileCheckNumber?.response1?.data,
        referrer_email: visitorMobileCheckNumber?.response1?.referrer_email,
      }));
    }
    //  else {
    //   // If the response is not valid, clear the name and email
    //   setForm((prevForm) => ({
    //     ...prevForm,
    //     referrer_name: "",
    //     referrer_email: "",
    //   }));
    // }
  }, [visitorMobileCheckNumber?.response1]);

  useEffect(() => {
    if (ethnicitySelector.response) {
      const { response } = ethnicitySelector;
      if (response?.status === 200) {
        const filteredEthnicitys = response.data;
        setEthnicityList(filteredEthnicitys);
      } else {
        setEthnicityList([]);
      }
    }
  }, [ethnicitySelector, viewDetails]);

  useEffect(() => {
    dispatch(getMasterList({ type: 13 }));
    dispatch(getEthnicity({ type: 15 }));
  }, [dispatch]);

  useEffect(() => {
    if (!user_id) {
      return;
    }
    // GRE: skip eager load — getAssignCPList is ~2.5MB and often 40–60s, which
    // starves getGreBannerImage / OTP on the same browser connection pool.
    if (isGREUser) {
      return;
    }
    dispatch(getAssignCPList({ user_id }));
  }, [dispatch, user_id, isGREUser]);

  // GRE: load CP companies only when the Channel Partner fields are actually needed.
  useEffect(() => {
    if (!isGREUser || !user_id || form.isSousingManagerLead) {
      return;
    }
    const needsCpList =
      form.lead_source === CONST_IDS?.cp_lead_source_id ||
      form.lead_source === channelPartnerMaster ||
      Number(form.cp_type) === 2;
    if (!needsCpList) {
      return;
    }
    dispatch(getAssignCPList({ user_id }));
  }, [
    dispatch,
    isGREUser,
    user_id,
    form.isSousingManagerLead,
    form.lead_source,
    form.cp_type,
  ]);

  useEffect(() => {
    if (cpUserList?.response?.status === 200 && !form.isSousingManagerLead) {
      setAgentList(cpUserList?.response?.data);
    } else {
      setAgentList([]);
    }
  }, [cpUserList, form.isSousingManagerLead]);

  useEffect(() => {
    if (employeeData?.emplist) {
      setEmployeeList(employeeData?.response);
    } else {
      setEmployeeList([]);
    }
  }, [employeeData]);

  useEffect(() => {
    if (!leadId) {
      dispatch(getAgentProperty());
    }
  }, [dispatch, leadId]);

  useEffect(() => {
    // Only hydrate from visitor detail when editing an existing lead.
    // Without this, persisted redux (hard reload) refills Add Visitor with the last lead.
    if (!leadId) {
      return;
    }
    if (visitorDetailSelector?.detail) {
      const { response } = visitorDetailSelector;

      // Check if the response is successful
      if (response?.status === 200) {
        const result = handleDetailResponse(response?.data);
        // Only update form if there is a change in the data
        setForm((prevForm) => {
          // Compare if the form data has changed to avoid unnecessary re-renders
          if (
            prevForm.first_name !== result?.customer_detail?.first_name ||
            prevForm.last_name !== result?.customer_detail?.last_name ||
            prevForm.mobile !== result?.customer_detail?.mobile
            // add more comparisons as necessary for other fields
          ) {
            return {
              ...prevForm,
              lead_id: leadId,
              create_by: result?.create_by,
              property_title: result?.property_title,
              property_id: result?.property_id,
              lead_source: result?.lead_source_id,
              cp_emp_id: result?.cp_emp_id,
              for_sm_id: result?.for_sm_id,
              for_sm_name: result?.created_for_sm_name || "",
              cp_id: result?.cp_id,
              configuration: result?.configuration,
              configuration_id: result?.configuration_id,
              expected_possession_date: result?.expected_possession_date
                ? moment(result?.expected_possession_date).format("YYYY-MM-DD")
                : null,
              preferred_bank: result?.customer_detail?.preferred_bank,
              property_type: result?.customer_detail?.property_type,
              areain_sqlft: result?.areain_sqlft,
              min_budget: result?.min_budget,
              min_emi_budget: result?.min_emi_budget,
              max_budget: result?.max_budget,
              max_emi_budget: result?.max_emi_budget,
              min_budget_type: result?.min_budget_type,
              min_emi_budget_type: result?.min_emi_budget_type,
              max_budget_type: result?.max_budget_type,
              max_emi_budget_type: result?.max_emi_budget_type,
              funding_type: result?.funding_type,
              purpose: result?.purpose,
              first_name: result?.customer_detail?.first_name,
              last_name: result?.customer_detail?.last_name,
              marital_status: result?.customer_detail?.marital_status,
              adhar_no: result?.customer_detail?.adhar_no,
              pancard_no: result?.customer_detail?.pancard_no,
              current_stay: result?.customer_detail?.current_stay,
              no_of_family_member: result?.customer_detail?.no_of_family_member,
              gender: result?.customer_detail?.gender,
              birth_date: result?.customer_detail?.birth_date
                ? moment(result?.customer_detail?.birth_date).format(
                    "YYYY-MM-DD",
                  )
                : null,
              mobile: result?.customer_detail?.mobile,
              whatsapp_no: result?.customer_detail?.whatsapp_no,
              email: result?.customer_detail?.email,
              location: result?.customer_detail?.location,
              locality: result?.customer_detail?.locality,
              occupation: result?.customer_detail?.occupation,
              income: result?.customer_detail?.income,
              desigantion: result?.customer_detail?.desigantion,
              coumpany_name: result?.customer_detail?.coumpany_name,
              office_address: result?.customer_detail?.office_address,
              referrer_contact: result?.referrer_contact,
              referrer_name: result?.referrer_name,
              referrer_email: result?.referrer_email,
              cp_lead_type: result?.cp_lead_type,
              referrel_partner: result?.referrel_partner,
              // LFSM only when an SM was actually stored — not merely CP/RP source
              // (SCM/SM create CP/RP without LFSM).
              isSousingManagerLead: !!result?.for_sm_id,
              ethnicity: result?.customer_detail?.ethnicity,
              ethnicity_id: result?.customer_detail?.ethnicity_id,
              visit_with: result?.customer_detail?.visit_with,
              expected_possession_period: result?.expected_possession_period,
              budget_amount: result?.budget_amount,
              lead_priority: result?.lead_priority,
              qualified: result?.qualified,
              draft: result?.draft,
            };
          }
          return prevForm; // Don't update if there is no change
        });
      }
    }
  }, [visitorDetailSelector, form, leadId]);

  useEffect(() => {
    leadId && setForm({ lead_id: leadId });
  }, [leadId]);

  const handleRightButton = () => {
    if (visitResponse?.status === 201) {
      setForm({ ...form, visit_confirmation_status: 1 });
    } else {
      if (visitResponse?.status === 202) {
        setForm({ ...form, visit_confirmation_status: 1 });
      }
    }
  };

  useEffect(() => {
    dispatch(getMasterList({ type: 13 }));
  }, [dispatch]);

  /**
   * JUSTO-64: Lead source filter for visitor add/edit.
   * GRE / CM / CTL / Closing Head: CP + Referral Partner only after LFSM is on
   * AND an SM is selected (for_sm_id). SCM / CSTL / sourcing roles: CP + RP always
   * visible (match app — SCM can create CP/RP without LFSM).
   */
  useEffect(() => {
    if (leadSourceSelector.response) {
      const { response } = leadSourceSelector;
      if (response?.status === 200) {
        let filteredData = response.data;
        setAllLeadSource(handleListResponse(filteredData));
        const effectiveRole = selectedRoleId || res?.data?.role_id;
        if (form.referrel_partner === 1) {
          form.lead_source = referralPartnerMaster;
        }
        if (
          userDetails?.rom &&
          [SourcingHead, SourcingManager, SourcingTL].includes(effectiveRole)
        ) {
          // For SourcingManager and SourcingTL, only Channel Partner and Reference should be included
          filteredData = filteredData.filter((item) =>
            [
              channelPartnerMaster,
              directWalkinMaster,
              exhibitionMaster,
              referralPartnerMaster,
              referenceMaster,
            ].includes(item._id),
          );
        } else if (
          [SourcingHead, SourcingManager, SourcingTL].includes(effectiveRole)
        ) {
          // For SourcingManager and SourcingTL, only Channel Partner and Referral Partner
          filteredData = filteredData.filter((item) =>
            [channelPartnerMaster, referralPartnerMaster].includes(item._id),
          );
        } else if (
          isCstlRoleId(effectiveRole) ||
          effectiveRole === SCM ||
          effectiveRole === scm_id
        ) {
          // CSTL / SCM: always include CP + Referral Partner with other sources
          // (match app — SCM can create CP/RP without LFSM).
        } else if (
          [ClosingManager, ClosingTL, closing_head_id].includes(effectiveRole)
        ) {
          // CM / CTL / CH: CP + Referral Partner only after SM is chosen (same as app).
          if (form.isSousingManagerLead && form.for_sm_id) {
            filteredData = filteredData.filter((item) =>
              [channelPartnerMaster, referralPartnerMaster].includes(item._id),
            );
          } else {
            filteredData = filteredData.filter(
              (item) =>
                ![channelPartnerMaster, referralPartnerMaster].includes(
                  item._id,
                ),
            );
          }
        } else {
          // GRE and others: same cascade — CP + Referral Partner only after SM.
          if (form.isSousingManagerLead && form.for_sm_id) {
            filteredData = filteredData.filter((item) =>
              [channelPartnerMaster, referralPartnerMaster].includes(item._id),
            );
          } else {
            filteredData = filteredData.filter(
              (item) =>
                ![channelPartnerMaster, referralPartnerMaster].includes(
                  item._id,
                ),
            );
          }
        }

        setleadSourceType(handleListResponse(filteredData));
      } else {
        setleadSourceType([]);
      }
    }
  }, [
    leadSourceSelector,
    res?.data?.role_id,
    userDetails?.rom,
    selectedRoleId,
    form,
  ]);

  const handleInputField = (e) => {
    if (e.target.name === "mobile" && requireGreMobileOtp) {
      setMobileOtpState(initialVisitorMobileOtpState);
      setForm((prevForm) => ({
        ...prevForm,
        mobile: e.target.value,
        visit_confirmation_status: "",
      }));
      return;
    }

    if (e.target.name === "first_name") {
      const raw = e.target.value;
      if (visitorNameInputHasInvalidChars(raw)) {
        errorToast(strings.nameValidErr);
      }
      const filtered = filterAlphabetsOnlyName(raw);
      e.target.value = filtered;
      setForm({
        ...form,
        first_name: filtered,
      });
      return;
    }

    if (e.target.name === "adhar_no") {
      let formattedText = e.target.value.split(" ").join("");
      if (formattedText.length > 0) {
        formattedText = formattedText
          .match(new RegExp(".{1,4}", "g"))
          .join(" ");
      }
      // props.disableSpecialCharacters === true &&
      // if (Regexs.alphaNumeric.test(val) === true) {
      //   props.onChangeText(formattedText);
      // }

      // Update the form state
      const nextFormState = {
        ...form,
        [e.target.name]: formattedText,
      };
      e.target.value = formattedText;
      setForm(nextFormState);
    } else {
      const nextFormState = {
        ...form,
        [e.target.name]: e.target.value,
      };
      setForm(nextFormState);
    }

    if (e.target.name === "lead_source") {
      let referrel_partner = null; // default value

      if (e.target.value === CONST_IDS?.ref_lead_source_id) {
        referrel_partner = 2;
      } else if (e.target.value === referralPartnerMaster) {
        referrel_partner = 1;
      }

      setForm((prevForm) => ({
        ...prevForm,
        cp_type: e.target.value === CONST_IDS?.cp_lead_source_id ? 2 : "",
        referrel_partner: referrel_partner,
        cp_id: null,
        cp_emp_id: "",
        referrer_name: "",
        referrer_email: "",
        referrer_contact: "",
        property_id: leadId || isGREUser ? prevForm.property_id : "",
        property_title: leadId || isGREUser ? prevForm.property_title : "",
        cp_lead_type: "",
        visit_confirmation_status: requireGreMobileOtp
          ? isGreMobileCurrentlyVerified(prevForm.mobile, mobileOtpState)
            ? 1
            : ""
          : 1,
        [e.target.name]: e.target.value,
      }));
    }

    if (e.target.name === "qualified") {
      const isQualified = e.target.value === "true";
      setForm((prevForm) => ({
        ...prevForm,
        [e.target.name]: isQualified,
      }));
      return;
    }

    if (e.target.name === "referrer_contact") {
      // If value has 10 digits, call the API to check referrer number
      if (e.target.value.length === 10) {
        dispatch(checkReferenceNmbrExist({ referrer_contact: e.target.value }));
      } else {
        // If the contact number is not valid, clear name and email
        setForm((prevForm) => ({
          ...prevForm,
          referrer_name: "",
          referrer_email: "",
          [e.target.name]: e.target.value, // Keep the current input value in the form
        }));
      }
    } else {
      // For other fields, simply update the form state
      setForm((prevForm) => ({
        ...prevForm,
        [e.target.name]: e.target.value,
      }));
    }
  };

  const handleWalkinFollowupDate = (date) => {
    setForm((prev) => ({
      ...prev,
      followup_date: date ? moment(date).format("YYYY-MM-DD") : "",
    }));
  };

  const handleWalkinFollowupTime = (followup_time) => {
    setForm((prev) => ({
      ...prev,
      followup_time: followup_time || "",
    }));
  };

  const handleInputField2 = (e) => {
    if (e.target.name === "adhar_no") {
      let formattedText = e.target.value.split(" ").join("");
      if (formattedText.length > 0) {
        formattedText = formattedText
          .match(new RegExp(".{1,4}", "g"))
          .join(" ");
      }
      // props.disableSpecialCharacters === true &&
      // if (Regexs.alphaNumeric.test(val) === true) {
      //   props.onChangeText(formattedText);
      // }

      // Update the form state
      const nextFormState = {
        ...form,
        [e.target.name]: formattedText,
      };
      e.target.value = formattedText;
      setForm(nextFormState);
    } else {
      const nextFormState = {
        ...form,
        [e.target.name]: e.target.value,
      };
      setForm(nextFormState);
    }

    if (e.target.name === "lead_source") {
      let referrel_partner = null; // default value

      if (e.target.value === CONST_IDS?.ref_lead_source_id) {
        referrel_partner = 2;
      } else if (e.target.value === referralPartnerMaster) {
        referrel_partner = 1;
      }

      setForm((prevForm) => ({
        ...prevForm,
        cp_type: e.target.value === CONST_IDS?.cp_lead_source_id ? 2 : "",
        referrel_partner: referrel_partner,
        cp_id: null,
        cp_emp_id: "",
        referrer_name: "",
        referrer_email: "",
        referrer_contact: "",
        // property_id: "",
        // property_title: "",
        cp_lead_type: "",
        [e.target.name]: e.target.value,
      }));
    }

    if (e.target.name === "referrer_contact") {
      // If value has 10 digits, call the API to check referrer number
      if (e.target.value.length === 10) {
        dispatch(checkReferenceNmbrExist({ referrer_contact: e.target.value }));
      } else {
        // If the contact number is not valid, clear name and email
        setForm((prevForm) => ({
          ...prevForm,
          referrer_name: "",
          referrer_email: "",
          [e.target.name]: e.target.value, // Keep the current input value in the form
        }));
      }
    } else {
      // For other fields, simply update the form state
      setForm((prevForm) => ({
        ...prevForm,
        [e.target.name]: e.target.value,
      }));
    }
  };

  const handleInputField1 = (e) => {
    const nextFormState = {
      ...form,
      cp_emp_id: "",
      [e.target.name]:
        e.target.name === "adhar_no"
          ? e.target.value
              .replace(/\D/g, "") // Remove non-digits
              .slice(0, 12) // Limit to 16 digits
              .split(/(\d{4})/) // Split into groups of 4 digits
              .filter(Boolean) // Remove empty strings
              .join(" ")
          : e.target.value,
    };
    SMteam
      ? setForm({ ...nextFormState, lead_source: form?.lead_source })
      : setForm(nextFormState);
  };

  const handleInputField3 = (e) => {
    const nextFormState = {
      ...form,
      cp_emp_id: "",
      [e.target.name]:
        e.target.name === "adhar_no"
          ? e.target.value
              .replace(/\D/g, "") // Remove non-digits
              .slice(0, 12) // Limit to 16 digits
              .split(/(\d{4})/) // Split into groups of 4 digits
              .filter(Boolean) // Remove empty strings
              .join(" ")
          : e.target.value,
    };
    setForm(nextFormState);
    // setForm({
    //   ...form,
    //   cp_id: e.target.value,
    // });
    if (e.target.value) {
      dispatch(getEmployeeList({ agency_id: e.target.value }));
    }
    // callEmploy()
  };

  useEffect(() => {
    // form.cp_id !== ""
    //   ? handleGetProperty(form.cp_id)
    //   : handleGetProperty(form?.cp_id);

    if (form.cp_id) {
      handleGetProperty(form.cp_id);
    }
  }, [form?.cp_id]);

  useEffect(() => {
    if (!propertyData?.response) {
      return;
    }

    const { response } = propertyData;
    if (
      !propertyData?.detail &&
      response?.status === 200 &&
      Array.isArray(response?.data) &&
      response.data.length > 0
    ) {
      setSourcingPropertyList(
        response.data
          .filter((el) => el?.status === true)
          .map((item) => ({
            ...item,
            property_id: normalizeMongoId(item?.property_id ?? item?._id),
          })),
      );
    } else if (!propertyData?.detail) {
      setSourcingPropertyList([]);
    }
  }, [propertyData]);

  const handleGetProperty = async (id) => {
    const params = {
      cp_id: id,
    };
    if (!leadId) {
      const res = await apiCall(
        "post",
        apiEndPoints.GET_CP_PROPERTY_FOR_SM,
        params,
      );
      const response = res?.data;

      if (response?.status === 200) {
        if (response?.data?.length > 0) {
          const list = sourcingPropertyList
            ?.filter((o1) =>
              response?.data?.some(
                (o2) =>
                  normalizeMongoId(o1?.property_id ?? o1?._id) ===
                  normalizeMongoId(o2?.property_id ?? o2?._id),
              ),
            )
            .map((item) => ({
              ...item,
              property_id: normalizeMongoId(item?.property_id ?? item?._id),
            }));
          setAllProperty(list);
        } else {
          setAllProperty([]);
        }
      } else {
        setAllProperty([]);
      }
    }
  };

  useEffect(() => {
    // Automatically set cp_type to 2 if the lead_source matches cp_lead_source_id
    if (form.lead_source === CONST_IDS?.cp_lead_source_id) {
      setForm((prevForm) => ({
        ...prevForm,
        cp_type: "2", // Set cp_type to 2
      }));
    }
  }, [form.lead_source]);

  const callEmploy = useCallback(() => {
    if (form.cp_id) {
      dispatch(getEmployeeList({ agency_id: form?.cp_id }));
    }
  }, [form?.cp_id]);

  // const handleInputArea = (e) => {
  //   const nextFormState = {
  //     ...form,
  //     location: e,
  //     latitude: "",
  //     longitude: "",
  //   };
  //   setForm(nextFormState);
  // };

  // LFSM: load SM options only when the toggle/property changes — not on every
  // for_sm_id / agentList update (that was stacking global loaders on SM select).
  useEffect(() => {
    if (isLfsmLead && form.property_id) {
      getPropertyBasedSMRoleDetails(form.property_id);
    } else if (isLfsmLead && !form.property_id) {
      setSmData([]);
    }
  }, [isLfsmLead, form.property_id]);

  // LFSM: CP companies under the selected SM (silent; effect owns the fetch).
  useEffect(() => {
    if (isLfsmLead && form.property_id && form.for_sm_id) {
      getSmRolesUnderCpData(form.for_sm_id, form.property_id);
    }
  }, [isLfsmLead, form.property_id, form.for_sm_id]);

  // Non-LFSM company list from assigned CPs / agent list.
  useEffect(() => {
    if (form.isSousingManagerLead) {
      return;
    }
    if (leadId) {
      const channelPartnerProperty = viewDetails.property_allocateds?.find(
        (item) => item.role_title === "Channel Partner",
      );
      const channelPartnerUserIds = channelPartnerProperty
        ? channelPartnerProperty.users.map((user) => user._id)
        : [];
      const filteredAgents = agentList?.filter((agent) =>
        channelPartnerUserIds.includes(agent._id),
      );
      setCompanyList(filteredAgents);
    } else {
      const tempArr = agentList?.filter((el) => el?.cp_type === 2);
      setCompanyList(tempArr);
    }
  }, [
    agentList,
    form.isSousingManagerLead,
    leadId,
    viewDetails.property_allocateds,
  ]);

  const handleCpNameDropdownPress = () => {
    const tempArr = agentList?.filter(
      (el) =>
        (el?.cp_type !== 2 || el?.cp_type === undefined) &&
        el?.active_status === true,
    );
    setDropdownAgentList(tempArr);
  };

  const handleEmployeeDropdownPress = () => {
    if (employeeData.emplist) {
      setEmployeeList(employeeData?.response);
    } else {
      setEmployeeList([]);
    }
  };

  // const onSelect = (latLng, location) => {
  //   const nextFormState = {
  //     ...form,
  //     location: location,
  //     latitude: latLng.lat,
  //     longitude: latLng.lng,
  //   };
  //   setForm(nextFormState);
  // };

  const handleGreCreate = async () => {
    if (requireGreMobileOtp && !isGreMobileVerified) {
      errorToast(strings.verifyMobileWithOtp);
      setFormVaild((prev) => ({
        ...prev,
        visit_confirmation_status: strings.verifyMobileWithOtp,
      }));
      return;
    }

    const formData = prepareVisitorSubmitPayload(form);
    // Keep for_sm_id when LFSM is selected (align with mobile GRE create).

    setIsLoading(true);
    createSubmitPendingRef.current = true;
    try {
      if (formData.property_title) {
        await dispatch(addVisitor(formData));
      } else {
        await dispatch(addVisitProperty(formData));
      }
    } catch (e) {
      createSubmitPendingRef.current = false;
    } finally {
      setIsLoading(false);
    }
  };

  const storePendingFollowUpIfNeeded = () => {
    if (
      showWalkinRemarkSection &&
      (form.walkin_remark || "").trim() &&
      (form.followup_date || "").trim() &&
      (form.followup_time || "").trim()
    ) {
      pendingFollowUpRef.current = {
        walkin_remark: (form.walkin_remark || "").trim(),
        followup_date: (form.followup_date || "").trim(),
        followup_time: (form.followup_time || "").trim(),
      };
    } else {
      pendingFollowUpRef.current = null;
    }
  };

  const handleStandardCreate = async ({ schedule = false } = {}) => {
    await ensureMdocClientConfigHydrated();
    // Backend expects visit_confirmation_status; non-GRE never runs OTP — default new visit.
    const formData = prepareVisitorSubmitPayload({
      ...form,
      ...(requireGreMobileOtp
        ? {}
        : { visit_confirmation_status: form.visit_confirmation_status || 1 }),
    });

    storePendingFollowUpIfNeeded();
    setCreateAndSchedule(!!schedule);
    createSubmitPendingRef.current = true;
    setIsLoading(true);
    try {
      if (formData.property_title) {
        await dispatch(addVisitor(formData));
      } else {
        await dispatch(addVisitProperty(formData));
      }
    } catch (_error) {
      createSubmitPendingRef.current = false;
      setCreateAndSchedule(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditUpdate = async () => {
    const formData = prepareVisitorSubmitPayload({
      ...form,
      lead_id: form.lead_id || leadId,
    });

    setIsLoading(true);
    editSubmitPendingRef.current = true;
    try {
      await dispatch(editVisitor(formData));
    } catch (_error) {
      editSubmitPendingRef.current = false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = () => {
    if (!handleValid()) {
      if (isGreSinglePageAdd) {
        handleGreCreate();
        return;
      }
      if (isSinglePageEdit) {
        handleEditUpdate();
        return;
      }
      if (isSinglePageAdd) {
        handleStandardCreate({ schedule: false });
        return;
      }
    }
  };
  const handleValid = () => {
    let err = {};
    let formError = false;
    if (!form.first_name) {
      formError = true;
      err["first_name"] = strings.firstName_Err;
    } else if (!nameValid(form.first_name)) {
      formError = true;
      err["first_name"] = strings.nameValidErr;
    }
    if (!form.mobile) {
      formError = true;
      err["mobile"] = strings.mobileErr;
    } else if (!mobileNumberValid(form.mobile)) {
      formError = true;
      err["mobile"] = strings.mobileValidErr;
    }
    if (isLfsmLead && !form.for_sm_id) {
      formError = true;
      err["for_sm_id"] = strings.sourcingManagerRequired;
    }
    if (!form.lead_source) {
      formError = true;
      err["lead_source"] = strings.leadSourceErr;
    }
    if (form.lead_source === CONST_IDS?.cp_lead_source_id) {
      if (!form.cp_lead_type) {
        formError = true;
        err["cp_lead_type"] = strings.chanelPartnerType;
      }
      if (!form.cp_id) {
        formError = true;
        err["cp_id"] = strings.chanelPartnerRequired;
      }
    }
    if (
      !form.referrer_contact &&
      (form.lead_source === CONST_IDS?.ref_lead_source_id ||
        form.lead_source === referralPartnerMaster)
    ) {
      formError = true;
      err["referrer_contact"] = strings.referrerRequired;
    }
    if (
      !form.referrer_name &&
      (form.lead_source === CONST_IDS?.ref_lead_source_id ||
        form.lead_source === referralPartnerMaster)
    ) {
      formError = true;
      err["referrer_name"] = strings.referrerRequired;
    }
    if (
      form.lead_source === CONST_IDS?.ref_lead_source_id ||
      form.lead_source === referralPartnerMaster
    ) {
      if (!mobileNumberValid(form.referrer_contact)) {
        formError = true;
        err["referrer_contact"] = strings.mobileValidErr;
      }
    }
    if (!form.property_id) {
      formError = true;
      err["property_id"] = strings.propertyRequire;
    }
    if (!form.gender) {
      formError = true;
      err["gender"] = strings.genderErr;
    }
    if (
      isMdocEmailRequired(
        form.property_id,
        form.property_title || greDefaultProperty?.property_title,
      ) &&
      !String(form.email || "").trim()
    ) {
      formError = true;
      err["email"] = strings.emailErr;
    }
    if (form.email && !emailAddressValid(form.email)) {
      formError = true;
      err["email"] = strings.emailValidationErr;
    }
    if (form.referrer_email && !emailAddressValid(form.referrer_email)) {
      formError = true;
      err["referrer_email"] = strings.emailValidationErr;
    }
    if (form.whatsapp_no && !mobileNumberValid(form.whatsapp_no)) {
      formError = true;
      err["whatsapp_no"] = strings.mobileValidErr;
    }
    if (requireGreMobileOtp) {
      if (!isGreMobileVerified) {
        formError = true;
        err["visit_confirmation_status"] = strings.verifyMobileWithOtp;
      }
    }
    // Non-GRE (SCM/CM/etc.): no mobile OTP / visit_confirmation gate — GRE-only.
    // CM/SCM create: walk-in remark + follow-up date/time required (parity with mobile)
    if (isSinglePageAdd && showWalkinRemarkSection) {
      if (!(form?.walkin_remark || "").trim()) {
        formError = true;
        err.walkin_remark = strings.walkinRemarkErr || strings.remarkErr;
      }
      if (!(form?.followup_date || "").trim()) {
        formError = true;
        err.followup_date = strings.followup_dateErr;
      }
      if (!(form?.followup_time || "").trim()) {
        formError = true;
        err.followup_time = strings.followup_timeErr;
      }
    }
    setFormVaild(err);

    if (formError) {
      showMissingRequiredFieldsToast(err);
    }

    return formError;
  };

  setTimeout(() => {
    setFormVaild();
  }, 20000); // Delay of 500ms (can be ad

  useEffect(() => {
    seo({
      title: "Visitor Form",
      metaDescription: "Visitor_form",
      canonicalLink: window.location.href,
    });
  }, []);

  const resolvePropertySelection = (propertyId) => {
    const normalizedId = normalizeMongoId(propertyId);
    const match = findPropertyInPools(normalizedId, [
      sourcingPropertyList,
      allProperty,
    ]);

    if (match) {
      return {
        property_id: normalizedId,
        property_title: match.property_title || "",
        gre_banner: match.gre_banner || null,
        gre_banner_url: match.gre_banner_url || null,
        has_banner: Boolean(match.gre_banner),
        banner_known: true,
        gre_mobile_otp_enabled: Boolean(match.gre_mobile_otp_enabled),
        configuration_area: match.configuration_area || null,
      };
    }

    return {
      property_id: normalizedId,
      property_title: "",
      gre_banner: null,
      gre_banner_url: null,
      has_banner: null,
      banner_known: false,
      gre_mobile_otp_enabled: false,
      configuration_area: null,
    };
  };

  const selectedPropertyHasBanner = useMemo(() => {
    const propertyId =
      form?.property_id || greDefaultProperty?.property_id || "";
    if (!isValidMongoObjectId(propertyId)) {
      return null;
    }
    const match = findPropertyInPools(propertyId, [
      sourcingPropertyList,
      allProperty,
    ]);
    if (!match) {
      if (
        viewDetails &&
        Object.prototype.hasOwnProperty.call(viewDetails, "gre_banner")
      ) {
        return Boolean(viewDetails.gre_banner);
      }
      return null;
    }
    return Boolean(match.gre_banner);
  }, [
    form?.property_id,
    greDefaultProperty?.property_id,
    sourcingPropertyList,
    allProperty,
    viewDetails,
  ]);

  const handlePropertySelectChange = (propertyId) => {
    const selection = resolvePropertySelection(propertyId);

    if (!isValidMongoObjectId(selection.property_id)) {
      setForm((prevForm) => ({
        ...prevForm,
        property_id: "",
        property_title: "",
        property_type_title: "",
      }));
      return;
    }

    const normalizedSelectionId = selection.property_id;
    const normalizedCurrentId = normalizeMongoId(form?.property_id || "");
    if (normalizedCurrentId === normalizedSelectionId) {
      return;
    }

    // Kick off banner blob immediately (shared cache with GreBannerImage).
    // getPropertyDetail waits for this via the property_id effect.
    const bannerPromise = preloadGreBannerImage(normalizedSelectionId, {
      hasBanner: selection.banner_known ? selection.has_banner : null,
    });

    // Seed OTP / config from list immediately — do not wait for deferred detail.
    if (isGREUser) {
      setViewDetails({
        gre_mobile_otp_enabled: selection.gre_mobile_otp_enabled,
        gre_banner: selection.gre_banner,
        ...(selection.configuration_area
          ? { configuration_area: selection.configuration_area }
          : {}),
      });
    }

    // Create: GRE+OTP must re-verify; everyone else stays confirmed (no OTP UI).
    const resolved = !leadId
      ? requireGreMobileOtp
        ? {
            otpState: { ...initialVisitorMobileOtpState },
            visitConfirmationStatus: "",
          }
        : {
            otpState: { ...initialVisitorMobileOtpState },
            visitConfirmationStatus: 1,
          }
      : resolveGrePropertyChangeOtpState(form?.mobile, mobileOtpState);

    setForm((prevForm) => ({
      ...prevForm,
      property_id: selection.property_id,
      property_title: selection.property_title,
      visit_confirmation_status: resolved.visitConfirmationStatus,
      ...(prevForm.isSousingManagerLead
        ? {
            for_sm_id: "",
            lead_source: "",
            cp_type: "",
            cp_id: "",
            cp_emp_id: "",
            cp_lead_type: "",
            referrer_name: "",
            referrer_email: "",
            referrer_contact: "",
            referrel_partner: "",
          }
        : {}),
    }));

    setMobileOtpState(resolved.otpState);

    // Mobile-check after banner preload so it does not steal the connection.
    bannerPromise.finally(() => {
      checkMobileExistWithSameProperty(normalizedSelectionId);
    });
    // SM list loads via the LFSM property effect (suppressGlobalLoader).
  };

  useEffect(() => {
    if (
      !isGreSinglePageAdd ||
      grePropertyAutoSelectedRef.current ||
      form.property_id ||
      !greDefaultProperty?.property_id
    ) {
      return;
    }

    grePropertyAutoSelectedRef.current = true;
    handlePropertySelectChange(greDefaultProperty.property_id);
  }, [
    isGreSinglePageAdd,
    form.property_id,
    greDefaultProperty?.property_id,
  ]);

  const getPropertyOptionValue = (item) =>
    normalizeMongoId(item?.property_id ?? item?._id);

  const checkMobileExistWithSameProperty = async (propertyId) => {
    const normalizedPropertyId = normalizeMongoId(propertyId);

    if (!isValidMongoObjectId(normalizedPropertyId) || !form?.mobile) {
      return false;
    }

    let params = {
      mobile: form?.mobile,
      property_id: normalizedPropertyId,
      suppressGlobalLoader: true,
    };

    try {
      const res = await apiCall(
        "post",
        apiEndPoints.CHECK_VISIT_MOB_AVAILABLE,
        params,
      );

      if (res?.data?.status === 200) {
        return true;
      } else if (res?.data?.status === 201) {
        errorToast(res?.data?.message);
        // setOkIsVisible(true);
        setTimeout(() => {
          setForm((prevForm) => ({
            ...prevForm,
            property_id: "",
            property_type_title: "",
            property_title: "",
            visit_confirmation_status: requireGreMobileOtp
              ? isGreMobileCurrentlyVerified(prevForm.mobile, mobileOtpState)
                ? 1
                : ""
              : 1,
          }));
        }, 500);
        return false;
      }
    } catch (e) {
      // OTP / mobile verification failed — keep form state; user can retry.
    }
  };

  const getPropertyBasedSMRoleDetails = async (propertyId) => {
    if (!propertyId) {
      setSmData([]);
      return;
    }
    let params = {
      property_id: propertyId,
      suppressGlobalLoader: true,
    };
    const res = await apiCall(
      "post",
      apiEndPoints.GET_PROPERTY_BASED_SM,
      params,
    );
    if (res?.data?.status === 200) {
      setSmData(res.data.data || []);
      // Do not switch GRE's selectedRoleId to SourcingManager — that collapses
      // lead-source options and can retrigger heavy list fetches/loaders.
      if (!isGREUser && res.data.data?.length > 0) {
        setSelectedRoleId(SourcingManager);
      }
    }
  };

  const getSmRolesUnderCpData = async (sm_id, property_Id) => {
    if (!sm_id) {
      setCompanyList([]);
      return;
    }
    let params = {
      sm_id: sm_id,
      property_id: form.property_id || property_Id,
      suppressGlobalLoader: true,
    };

    try {
      const res = await apiCall("post", apiEndPoints.CP_UNDER_PROPERTY, params);
      if (res?.data?.status === 200) {
        setCompanyList(res?.data?.data);
      } else {
        setCompanyList([]);
      }
    } catch (error) {
      // Keep company list empty on failure; form stays interactive without a toast spam.
      setCompanyList([]);
    }
  };

  const handleLeadTypeChange = (e) => {
    const { value } = e.target;

    // Map the selected option to the corresponding numeric value
    setForm({
      ...form,
      cp_lead_type: value,
    });
  };

  const renderLeadForSmReadOnly = (wrapperClassName = "form-check mb-3") => (
    <div className={wrapperClassName}>
      <input
        type="checkbox"
        className="form-check-input"
        id="housingManagerCheckboxReadOnly"
        checked
        readOnly
        disabled
      />
      <label
        className="form-check-label"
        htmlFor="housingManagerCheckboxReadOnly"
      >
        {strings.leadForSoursingManager}
        {isGREUser && (
          <span className="gre-sm-lead-hint"> {strings.leadForSmGreHint}</span>
        )}
      </label>
    </div>
  );

  const renderLeadForSmCheckbox = (wrapperClassName = "form-check mb-3") => (
    <div className={wrapperClassName}>
      <input
        type="checkbox"
        className="form-check-input"
        id="housingManagerCheckbox"
        checked={form.isSousingManagerLead}
        disabled={leadId && !edit && !Cmteam}
        onChange={(e) => {
          const isChecked = e.target.checked;
          const preserveGreProperty = isGREUser && !leadId;
          // Only a completed OTP session counts — never promote
          // visit_confirmation_status=1 into a green "verified" state.
          const otpVerified = isGreMobileCurrentlyVerified(
            form?.mobile,
            mobileOtpState,
          );
          const nextVisitStatus = otpVerified
            ? 1
            : requireGreMobileOtp
              ? ""
              : 1;

          if (!isChecked) {
            // Restore real login role (never leave GRE on SourcingManager).
            setSelectedRoleId(res?.data?.role_id);
            setSmData([]);
            setForm((prevForm) => ({
              ...prevForm,
              isSousingManagerLead: false,
              lead_source: "",
              property_id:
                leadId || preserveGreProperty ? prevForm.property_id : "",
              property_title:
                leadId || preserveGreProperty ? prevForm.property_title : "",
              referrel_partner: "",
              for_sm_id: "",
              cp_type: "",
              cp_id: "",
              cp_emp_id: "",
              cp_lead_type: "",
              referrer_name: "",
              referrer_email: "",
              referrer_contact: "",
              visit_confirmation_status: nextVisitStatus,
            }));
          } else {
            // GRE must keep selectedRoleId as GRE — switching to SourcingManager
            // collapses lead sources and retriggers heavy list/loader fetches.
            if (!isGREUser) {
              setSelectedRoleId(SourcingManager);
            }
            setForm((prevForm) => ({
              ...prevForm,
              isSousingManagerLead: true,
              lead_source: "",
              for_sm_id: "",
              property_id:
                leadId || preserveGreProperty ? prevForm.property_id : "",
              property_title:
                leadId || preserveGreProperty ? prevForm.property_title : "",
              referrel_partner: "",
              cp_type: "",
              cp_id: "",
              cp_emp_id: "",
              cp_lead_type: "",
              referrer_name: "",
              referrer_email: "",
              referrer_contact: "",
              visit_confirmation_status: nextVisitStatus,
            }));
          }
        }}
      />
      <label className="form-check-label" htmlFor="housingManagerCheckbox">
        {strings.leadForSoursingManager}
        {isGREUser && (
          <span className="gre-sm-lead-hint"> {strings.leadForSmGreHint}</span>
        )}
      </label>
    </div>
  );

  return (
    <div
      className={`page-body${isGreSinglePageAdd ? " gre-visitor-form-page" : isJustoSinglePage ? " justo-visitor-page" : ""}`}
    >
      <Breadcrumb
        title={leadId ? strings.editvisitor : strings.addNewVisitor}
        url={"/visitor"}
        parentListing={strings.visitorList}
        titleactive={leadId ? strings.editVisitor : strings.addNewVisitor}
        isBackBtn={true}
      />
      {/*add_module_start */}
      {visitModule ? (
        <div
          className="modal show"
          tabIndex="-1"
          style={{
            display: "block",
          }}
          role="dialog"
          aria-labelledby="exampleModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="exampleModalLabel">
                  {strings.conformation}
                </h5>
                <button
                  className="close"
                  type="button"
                  data-dismiss="modal"
                  aria-label="Close"
                  onClick={() => {
                    setVisitModule(false);
                    setForm({ ...form, visit_confirmation_status: "" });
                  }}
                >
                  <span aria-hidden="true">×</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="text-center">
                  <h6>
                    {visitResponse?.message === "" ||
                    visitResponse?.message === undefined ||
                    visitResponse?.message === null
                      ? strings.notavailable
                      : visitResponse?.message}
                  </h6>
                </div>
              </div>
              <div className="modal-footer justify-content-center">
                <button
                  className="btn btn-success mr-5"
                  type="button"
                  onClick={() => {
                    setVisitModule(false);
                    // handleLeftButton();
                  }}
                  data-dismiss="modal"
                >
                  {visitResponse?.status === 201
                    ? strings.no
                    : visitResponse?.status === 202
                      ? strings.useOldData
                      : strings.notavailable}
                </button>
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={() => {
                    setVisitModule(false);
                    handleRightButton();
                  }}
                  data-dismiss="modal"
                >
                  {visitResponse?.status === 201
                    ? strings.yes
                    : visitResponse?.status === 202
                      ? strings.createNew
                      : strings.notavailable}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {/* add_module_end */}
      <div
        className={`container-fluid${isGreSinglePageAdd ? " gre-visitor-form-container" : isJustoSinglePage ? " justo-visitor justo-visitor-form" : ""}`}
      >
        <div
          className={`row${isGreSinglePageAdd || isJustoSinglePage ? " justify-content-center" : ""}`}
        >
          <div
            className={
              isGreSinglePageAdd
                ? "col-12 col-lg-10 col-xl-7"
                : isJustoSinglePage
                  ? "col-12"
                  : "col-sm-12"
            }
          >
            <div
              className={
                isJustoSinglePage
                  ? "justo-visitor__card justo-visitor-form__card"
                  : `card${isGreSinglePageAdd ? " gre-visitor-form-card" : ""}`
              }
            >
              <div
                className={
                  isJustoSinglePage
                    ? "justo-visitor-form__header"
                    : `card-header pb-0${isGreSinglePageAdd ? " gre-visitor-form-header border-0" : ""}`
                }
              >
                {isJustoSinglePage ? (
                  <div className="justo-visitor__top justo-visitor-form__top">
                    <div className="justo-visitor__title-block">
                      <h1 className="justo-visitor__title">
                        {leadId ? strings.editvisitor : strings.addNewVisitor}
                      </h1>
                      <p className="justo-visitor__subtitle">
                        {leadId
                          ? "Update lead, customer, and property preference details."
                          : "Enter lead, customer, and property preference details."}
                      </p>
                    </div>
                  </div>
                ) : (
                  <h5>{leadId ? strings.editvisitor : strings.addNewVisitor}</h5>
                )}
                {isGreSinglePageAdd && (
                  <>
                    <p className="gre-visitor-form-lead">
                      Please enter the visitor and property details.
                    </p>
                    <GreVisitorFormStepBar
                      steps={[strings.visitorDetails, strings.propertyRequired]}
                    />
                  </>
                )}
              </div>
              <form className="form theme-form">
                <div
                  className={`${
                    isJustoSinglePage
                      ? "justo-visitor-form__body justo-visitor-form__body--edit"
                      : `card-body${isGreSinglePageAdd ? " gre-visitor-form-body" : ""}`
                  }`}
                >
                  {showGreFormBanner ? (
                    <PropertyDeveloperBanner
                      propertyId={
                        form.property_id || greDefaultProperty?.property_id
                      }
                      propertyTitle={
                        form.property_title || greDefaultProperty?.property_title
                      }
                      hasBanner={selectedPropertyHasBanner}
                      className="gre-visitor-form-banner"
                    />
                  ) : null}
                  <div
                    className={
                      isGreSinglePageAdd
                        ? "gre-visitor-form-panel gre-visitor-form-panel--visitor"
                        : isJustoSinglePage
                          ? "justo-visitor-form__columns"
                          : undefined
                    }
                    style={
                      !isGreSinglePageAdd && !isJustoSinglePage
                        ? { display: "contents" }
                        : undefined
                    }
                  >
                    <div
                      className={
                        isJustoSinglePage ? "justo-visitor-form__column" : undefined
                      }
                    >
                      {isJustoSinglePage ? (
                        <h2 className="justo-visitor-form__section-title">
                          {strings.visitorDetails}
                        </h2>
                      ) : null}
                    {isGreSinglePageAdd && (
                      <GreVisitorFormSectionHeader
                        step="1"
                        title={strings.visitorDetails}
                      />
                    )}
                    {leadId ? (
                      <div className="row ">
                        <div className="col-md-8">
                          <div className="mb-3">
                            <label htmlFor="exampleFormControlFirstName">
                              {strings.visitorFullName}
                              <span className="required-field"></span>
                            </label>
                            <input
                              className="form-control"
                              id="exampleFormControlFirstName"
                              type="text"
                              name="first_name"
                              value={form?.first_name}
                              placeholder={strings.visitorFullName}
                              onChange={handleInputField}
                              autoComplete="off"
                            />
                            {formVaild?.first_name && (
                              <span style={{ color: "red" }}>
                                {formVaild?.first_name}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="row ">
                        <div className="col-md-8">
                          <div className="mb-3">
                            <label htmlFor="exampleFormControlFirstName">
                              {strings.visitorFullName}
                              <span className="required-field"></span>
                            </label>
                            <input
                              className="form-control"
                              id="exampleFormControlFirstName"
                              type="text"
                              name="first_name"
                              value={form?.first_name}
                              placeholder={strings.visitorFullName}
                              onChange={handleInputField}
                              autoComplete="off"
                            />
                            {formVaild?.first_name && (
                              <span style={{ color: "red" }}>
                                {formVaild?.first_name}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="row">
                      <div className="col-md-8">
                        <div
                          className={`mb-3${requireGreMobileOtp ? " gre-mobile-field" : ""}`}
                        >
                          <label htmlFor="exampleInputMobileNo">
                            {strings.mobileNo}
                            <span className="required-field"></span>
                          </label>
                          {requireGreMobileOtp ? (
                            <p className="gre-mobile-field__helper">
                              {strings.mobileNoWithoutCountryCodeHint}
                            </p>
                          ) : null}
                          <div
                            className={`gre-mobile-row${
                              useGreMobileSplitLayout ? "" : " gre-mobile-row--full"
                            }`}
                          >
                            <div className="gre-mobile-row__phone">
                              {requireGreMobileOtp ? (
                                <div
                                  className={`input-group gre-mobile-input-group${
                                    isGreMobileVerified
                                      ? " gre-mobile-input-group--verified"
                                      : ""
                                  }${
                                    mobileOtpState.otpSent
                                      ? " gre-mobile-input-group--locked"
                                      : ""
                                  }`}
                                >
                                  <span className="input-group-text">+91</span>
                                  <input
                                    className="form-control"
                                    id="exampleInputMobileNo"
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={10}
                                    name="mobile"
                                    value={form?.mobile || ""}
                                    placeholder={strings.mobilePlaceholderGre}
                                    onChange={handleGreMobileInput}
                                    autoComplete="tel-national"
                                    readOnly={mobileOtpState.otpSent}
                                    onFocus={() => setVisitResponse({})}
                                  />
                                  {isGreMobileVerified ? (
                                    <span className="input-group-text gre-mobile-input-group__badge">
                                      <i
                                        className="fa fa-check"
                                        aria-hidden="true"
                                      />
                                    </span>
                                  ) : null}
                                </div>
                              ) : (
                                <input
                                  className="form-control"
                                  id="exampleInputMobileNo"
                                  readOnly={
                                    isCreated
                                      ? false
                                      : form?.lead_id
                                        ? true
                                        : false
                                  }
                                  type="text"
                                  maxLength={10}
                                  name="mobile"
                                  value={
                                    isSuperAdmin || !form?.lead_id || isCreated
                                      ? form?.mobile || ""
                                      : form?.mobile
                                        ? hideMobileNumber(form?.mobile)
                                        : ""
                                  }
                                  placeholder={strings.mobileNo}
                                  onChange={handleInputField}
                                  autoComplete="off"
                                  onKeyPress={(event) => {
                                    if (/^[a-z]+$/.test(event.key)) {
                                      event.preventDefault();
                                    }
                                  }}
                                  onFocus={() => setVisitResponse({})}
                                  onBlur={() => {
                                    if (
                                      !form?.lead_id &&
                                      form?.mobile?.length === 10
                                    ) {
                                      dispatch(
                                        visitorMobileCheck({
                                          mobile: form.mobile,
                                          property_id: form.property_id,
                                          visitor_name: form.first_name,
                                          cp_id: form.cp_id,
                                        }),
                                      );
                                    }
                                  }}
                                />
                              )}
                            </div>
                            {useGreMobileSplitLayout ? (
                              <div className="gre-mobile-row__aside">
                                {showInlineVerifyButton ? (
                                  <MobileOtpVerification
                                    variant="gre"
                                    inlineSendOnly
                                    mobile={form?.mobile}
                                    verifiedMobile={
                                      mobileOtpState.verifiedMobile
                                    }
                                    isMobileValid={isVisitorMobileValid(
                                      form?.mobile,
                                    )}
                                    showSendOtp={showGreSendOtp}
                                    mobileVerified={isGreMobileVerified}
                                    otpSent={mobileOtpState.otpSent}
                                    otp={mobileOtpState.otp}
                                    sendingOtp={mobileOtpState.sendingOtp}
                                    verifyingOtp={mobileOtpState.verifyingOtp}
                                    sendError={mobileOtpState.otpSendError}
                                    onSendOtp={handleSendVisitorOtp}
                                    onVerifyOtp={handleVerifyVisitorOtp}
                                    onResendOtp={handleResendVisitorOtp}
                                    onOtpChange={handleVisitorOtpChange}
                                    onChangeMobile={
                                      handleResetGreMobileVerification
                                    }
                                  />
                                ) : null}
                                {showLeadForSmBesideMobile
                                  ? renderLeadForSmCheckbox(
                                      "form-check gre-mobile-row__sm-check",
                                    )
                                  : null}
                              </div>
                            ) : null}
                          </div>
                          {formVaild?.mobile && (
                            <span style={{ color: "red" }}>
                              {formVaild?.mobile}
                            </span>
                          )}
                          {formVaild?.visit_confirmation_status && (
                            <span style={{ color: "red" }}>
                              {formVaild?.visit_confirmation_status}
                            </span>
                          )}
                          {requireGreMobileOtp ? (
                            <MobileOtpVerification
                              variant="gre"
                              suppressSendButton
                              mobile={form?.mobile}
                              verifiedMobile={mobileOtpState.verifiedMobile}
                              isMobileValid={isVisitorMobileValid(form?.mobile)}
                              showSendOtp={showGreSendOtp}
                              mobileVerified={isGreMobileVerified}
                              otpSent={mobileOtpState.otpSent}
                              otp={mobileOtpState.otp}
                              sendingOtp={mobileOtpState.sendingOtp}
                              verifyingOtp={mobileOtpState.verifyingOtp}
                              sendError={mobileOtpState.otpSendError}
                              onSendOtp={handleSendVisitorOtp}
                              onVerifyOtp={handleVerifyVisitorOtp}
                              onResendOtp={handleResendVisitorOtp}
                              onOtpChange={handleVisitorOtpChange}
                              onChangeMobile={handleResetGreMobileVerification}
                            />
                          ) : null}
                        </div>
                      </div>
                    </div>

                    {showLeadForSmSeparateRow ? (
                      <div className="row">
                        <div className="col-md-8">
                          {renderLeadForSmCheckbox()}
                        </div>
                      </div>
                    ) : null}

                    {showLeadForSmOnEdit ? (
                      <div className="row">
                        <div className="col-md-8">{renderLeadForSmReadOnly()}</div>
                      </div>
                    ) : null}

                    {isLfsmLead ? (
                      <div>
                        {leadId ? (
                          <div className="row ">
                            <div className="col-md-8 ">
                              <div className="mb-3">
                                <label htmlFor="exampleFormControlFirstName">
                                  {strings.propertyname}
                                  <span className="required-field"></span>
                                </label>
                                <input
                                  className="form-control"
                                  id="exampleFormControlFirstName"
                                  type="text"
                                  name="property_title"
                                  value={form.property_title}
                                  readOnly
                                  placeholder={strings.propertyname}
                                  onChange={handleInputField}
                                />
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="row ">
                            <div className="col-md-8 ">
                              <div className="mb-3">
                                <label htmlFor="exampleFormControlFirstName">
                                  {strings.propertyname}
                                  <span className="required-field"></span>
                                </label>
                                <select
                                  className="form-control digits"
                                  id="formcontrol-select1"
                                  name="property_id"
                                  value={form.property_id}
                                  onFocus={() =>
                                    form?.mobile?.length === 10
                                      ? null
                                      : errorToast(strings.mobileErr)
                                  }
                                  onChange={(e) => {
                                    handlePropertySelectChange(e.target.value);
                                  }}
                                >
                                  <option defaultValue={null}>
                                    {strings.selectProp}
                                  </option>
                                  {form?.mobile?.length === 10
                                    ? Cmteam ||
                                      ((mixTeam || SMteam) &&
                                        form.lead_source !==
                                          CONST_IDS?.cp_lead_source_id)
                                      ? sourcingPropertyList.map(
                                          (item, index) => (
                                            <option
                                              key={index}
                                              value={getPropertyOptionValue(item)}
                                            >
                                              {item.property_title}
                                            </option>
                                          ),
                                        )
                                      : allProperty &&
                                        allProperty.map((item, index) => (
                                          <option
                                            key={index}
                                            value={getPropertyOptionValue(item)}
                                          >
                                            {item.property_title}
                                          </option>
                                        ))
                                    : emptyArray.map((item, index) => (
                                        <option
                                          key={index}
                                          value={getPropertyOptionValue(item)}
                                        >
                                          {item.property_title}
                                        </option>
                                      ))}
                                </select>
                              </div>
                            </div>
                          </div>
                        )}

                        {form.property_id ? (
                        <div className="row">
                          <div className="col-md-8">
                            <div className="mb-3">
                              <label className="form-label fw-bold">
                                {strings.selectSM_Manager}
                                <span className="required-field"></span>
                              </label>
                              <select
                                className="form-control"
                                name="sourcing_manager_id"
                                value={form.for_sm_id}
                                disabled={leadId && !isLfsmLead}
                                onChange={(e) => {
                                  const selectedId = e.target.value;
                                  const selectedSM = smData.find(
                                    (sm) => sm._id === selectedId,
                                  );
                                  const sm_id = selectedSM?._id || null;

                                  setSelectedSMRoleId(sm_id);
                                  // Edit: only correct SM — keep lead source / CP / referrer.
                                  // Create: clear cascade fields (same as mobile LFSM flow).
                                  if (leadId) {
                                    setForm({
                                      ...form,
                                      for_sm_id: selectedId,
                                      for_sm_name:
                                        selectedSM?.user_name ||
                                        form.for_sm_name ||
                                        "",
                                    });
                                    return;
                                  }
                                  // CP under SM loads via the for_sm_id effect (silent).
                                  setForm({
                                    ...form,
                                    for_sm_id: selectedId,
                                    lead_source: "",
                                    cp_emp_id: "",
                                    cp_id: "",
                                    cp_type: "",
                                    cp_lead_type: "",
                                    referrer_name: "",
                                    referrer_email: "",
                                    referrer_contact: "",
                                    referrel_partner: "",
                                  });
                                }}
                              >
                                <option value="">
                                  {strings.selectSourcingManager}
                                </option>
                                {form.for_sm_id &&
                                !smData.some(
                                  (sm) =>
                                    String(sm._id) === String(form.for_sm_id),
                                ) ? (
                                  <option value={form.for_sm_id}>
                                    {form.for_sm_name ||
                                      strings.sourcingManager}
                                  </option>
                                ) : null}
                                {smData.map((sm) => (
                                  <option key={sm._id} value={sm._id}>
                                    {sm.user_name}
                                  </option>
                                ))}
                              </select>
                              {formVaild?.for_sm_id && (
                                <span style={{ color: "red" }}>
                                  {formVaild.for_sm_id}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        ) : null}

                        {form.for_sm_id ? (
                        <>
                        <div className="row">
                          <div className="col-md-8">
                            <div className="mb-3">
                              <label htmlFor="exampleFormControlLastName">
                                {strings.leadSource}
                                {isGREUser && (
                                  <span className="gre-sm-lead-hint">
                                    {" "}
                                    {strings.leadForSmGreHint}
                                  </span>
                                )}
                                <span className="required-field"></span>
                              </label>
                              {/* JUSTO-64: After SM is selected, show CP / Referral Partner as radios. */}
                              {form.for_sm_id &&
                              leadSourceType &&
                              leadSourceType.length === 2 ? (
                                <div
                                  className="d-flex flex-wrap"
                                  style={{ gap: "1rem" }}
                                >
                                  {leadSourceType.map((item) => (
                                    <label
                                      key={item._id}
                                      className="d-flex align-items-center cursor-pointer"
                                      style={{ gap: "0.5rem" }}
                                    >
                                      <input
                                        type="radio"
                                        name="lead_source"
                                        value={item._id}
                                        checked={form.lead_source === item._id}
                                        onChange={(e) =>
                                          handleInputField2({
                                            target: {
                                              name: "lead_source",
                                              value: e.target.value,
                                            },
                                          })
                                        }
                                        disabled={
                                          leadId &&
                                          !edit &&
                                          !form.isSousingManagerLead
                                        }
                                      />
                                      {item.title}
                                    </label>
                                  ))}
                                </div>
                              ) : (
                                <select
                                  className="form-control digits"
                                  id="formcontrol-select1"
                                  name="lead_source"
                                  value={form.lead_source}
                                  onChange={handleInputField2}
                                  disabled={
                                    leadId &&
                                    !edit &&
                                    !form.isSousingManagerLead
                                  }
                                >
                                  <option value="">
                                    {strings.selectLeadSource}
                                  </option>
                                  {leadSourceType &&
                                    leadSourceType.map((item, index) => (
                                      <option key={index} value={item._id}>
                                        {item.title}
                                      </option>
                                    ))}
                                </select>
                              )}
                              {formVaild?.lead_source && (
                                <span style={{ color: "red" }}>
                                  {formVaild?.lead_source}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* JUSTO-64: CP lead type (Walk-in / Tele-calling) shown as radio buttons when lead source is Channel Partner. */}
                        {form.cp_type === "2" &&
                        form.lead_source === CONST_IDS?.cp_lead_source_id ? (
                          <>
                            <div className="row">
                              <div className="col-md-8">
                                <div className="mb-3">
                                  <label htmlFor="cpLeadType">
                                    {strings.cpLeadType}
                                    <span className="required-field"></span>
                                  </label>
                                  <div
                                    className="d-flex flex-wrap"
                                    style={{ gap: "1rem" }}
                                  >
                                    <label
                                      className="d-flex align-items-center cursor-pointer"
                                      style={{ gap: "0.5rem" }}
                                    >
                                      <input
                                        type="radio"
                                        name="cp_lead_type"
                                        value="1"
                                        checked={form.cp_lead_type === "1"}
                                        onChange={handleLeadTypeChange}
                                      />
                                      {strings.walk_in}
                                    </label>
                                    <label
                                      className="d-flex align-items-center cursor-pointer"
                                      style={{ gap: "0.5rem" }}
                                    >
                                      <input
                                        type="radio"
                                        name="cp_lead_type"
                                        value="2"
                                        checked={form.cp_lead_type === "2"}
                                        onChange={handleLeadTypeChange}
                                      />
                                      {strings.teleCalling}
                                    </label>
                                  </div>
                                  {formVaild?.cp_lead_type && (
                                    <span style={{ color: "red" }}>
                                      {formVaild?.cp_lead_type}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            {leadId ? (
                              mixTeam || SMteam ? (
                                <div className="row">
                                  <div className="col-md-8">
                                    <div className="mb-3">
                                      <label htmlFor="exampleFormControlLastName">
                                        {strings.cpCompany}
                                        <span className="required-field"></span>
                                      </label>
                                      <select
                                        className="form-control digits"
                                        id="formcontrol-select1"
                                        name="cp_id"
                                        value={form.cp_id}
                                        onChange={handleInputField1}
                                        // onFocus={() => handleCompanyDropdownPress()}
                                        onBlur={() => callEmploy()}
                                        // isSearchable={true}
                                        // disabled={true}
                                      >
                                        <option value="">
                                          {strings.selectCpCompany}
                                        </option>
                                        {companyList &&
                                          companyList.map((item, index) => (
                                            <option
                                              key={index}
                                              value={item._id}
                                            >
                                              {item.agency_name}
                                            </option>
                                          ))}
                                      </select>
                                      {formVaild?.cp_id && (
                                        <span style={{ color: "red" }}>
                                          {formVaild?.cp_id}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="row">
                                  <div className="col-md-8">
                                    <div className="mb-3">
                                      <label htmlFor="exampleFormControlLastName">
                                        {strings.cpCompany}
                                        <span className="required-field"></span>
                                      </label>
                                      <select
                                        className="form-control digits"
                                        id="formcontrol-select1"
                                        name="cp_id"
                                        value={form.cp_id}
                                        onChange={handleInputField1}
                                        // onFocus={() => handleEmployeeDropdownPress()}
                                        onBlur={() => callEmploy()}
                                        // isSearchable={true}
                                        // disabled={true}
                                      >
                                        <option value="">
                                          {strings.selectCpCompany}
                                        </option>
                                        {companyList &&
                                          companyList.map((item, index) => (
                                            <option
                                              key={index}
                                              value={item._id}
                                            >
                                              {item.agency_name}
                                            </option>
                                          ))}
                                      </select>
                                      {formVaild?.cp_id && (
                                        <span style={{ color: "red" }}>
                                          {formVaild?.cp_id}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )
                            ) : (
                              <div className="row">
                                <div className="col-md-8">
                                  <div className="mb-3">
                                    <label htmlFor="exampleFormControlLastName">
                                      {strings.cpCompany}
                                      <span className="required-field"></span>
                                    </label>
                                    <select
                                      className="form-control digits"
                                      id="formcontrol-select1"
                                      name="cp_id"
                                      value={form.cp_id}
                                      onChange={handleInputField1}
                                      // onFocus={() => handleCompanyDropdownPress()}
                                      onBlur={() => callEmploy()}
                                      // isSearchable={true}
                                    >
                                      <option value="">
                                        {strings.selectCpCompany}
                                      </option>
                                      {companyList &&
                                        companyList.map((item, index) => (
                                          <option key={index} value={item._id}>
                                            {item.agency_name}
                                          </option>
                                        ))}
                                    </select>
                                    {formVaild?.cp_id && (
                                      <span style={{ color: "red" }}>
                                        {formVaild?.cp_id}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}

                            {form?.cp_id !== "" ||
                            form.lead_source ===
                              CONST_IDS?.cp_lead_source_id ? (
                              <>
                                {leadId ? (
                                  mixTeam || SMteam ? (
                                    <div className="row">
                                      <div className="col-md-8">
                                        <div className="mb-3">
                                          <label htmlFor="exampleFormControlLastName">
                                            {strings.companyEmployName}
                                            {/* <span className="required-field"></span> */}
                                          </label>
                                          <select
                                            className="form-control digits"
                                            id="formcontrol-select1"
                                            name="cp_emp_id"
                                            value={form.cp_emp_id}
                                            onChange={handleInputField}
                                            onFocus={() =>
                                              handleEmployeeDropdownPress()
                                            }
                                            // disabled={true}
                                          >
                                            <option value="">
                                              {strings.selectEmployeeName}
                                            </option>
                                            {employeeList &&
                                              employeeList.map(
                                                (item, index) => (
                                                  <option
                                                    key={index}
                                                    value={item.login_id}
                                                  >
                                                    {item.employee_name}
                                                  </option>
                                                ),
                                              )}
                                          </select>
                                          {formVaild?.cp_emp_id && (
                                            <span style={{ color: "red" }}>
                                              {formVaild?.cp_emp_id}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="row">
                                      <div className="col-md-8">
                                        <div className="mb-3">
                                          <label htmlFor="exampleFormControlLastName">
                                            {strings.companyEmployName}
                                            {/* <span className="required-field"></span> */}
                                          </label>
                                          <select
                                            className="form-control digits"
                                            id="formcontrol-select1"
                                            name="cp_emp_id"
                                            value={form.cp_emp_id}
                                            onChange={handleInputField}
                                            onFocus={() =>
                                              handleEmployeeDropdownPress()
                                            }
                                            // disabled={true}
                                          >
                                            <option value="">
                                              {strings.selectEmployeeName}
                                            </option>
                                            {employeeList &&
                                              employeeList.map(
                                                (item, index) => (
                                                  <option
                                                    key={index}
                                                    value={item.login_id}
                                                  >
                                                    {item.employee_name}
                                                  </option>
                                                ),
                                              )}
                                          </select>
                                          {formVaild?.cp_emp_id && (
                                            <span style={{ color: "red" }}>
                                              {formVaild?.cp_emp_id}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )
                                ) : (
                                  <div className="row">
                                    <div className="col-md-8">
                                      <div className="mb-3">
                                        <label htmlFor="exampleFormControlLastName">
                                          {strings.companyEmployName}
                                          {/* <span className="required-field"></span> */}
                                        </label>
                                        <select
                                          className="form-control digits"
                                          id="formcontrol-select1"
                                          name="cp_emp_id"
                                          value={form.cp_emp_id}
                                          onChange={handleInputField}
                                          onFocus={() =>
                                            handleEmployeeDropdownPress()
                                          }
                                        >
                                          <option value="">
                                            {strings.selectEmployeeName}
                                          </option>
                                          {employeeList &&
                                            employeeList.map((item, index) => (
                                              <option
                                                key={index}
                                                value={item.login_id}
                                              >
                                                {item.employee_name}
                                              </option>
                                            ))}
                                        </select>
                                        {formVaild?.cp_emp_id && (
                                          <span style={{ color: "red" }}>
                                            {formVaild?.cp_emp_id}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </>
                            ) : null}
                          </>
                        ) : null}
                        {form.lead_source === CONST_IDS?.ref_lead_source_id ||
                        form.lead_source === referralPartnerMaster ? (
                          <>
                            <div className="row">
                              <div className="col-md-8">
                                <div className="mb-3">
                                  <label htmlFor="exampleInputMobileNo">
                                    {strings.referrerNumber}
                                    <span className="required-field"></span>
                                  </label>
                                  <input
                                    className="form-control"
                                    id="exampleInputMobileNo"
                                    // readOnly={
                                    //   isCreated
                                    //     ? false
                                    //     : form?.lead_id
                                    //     ? true
                                    //     : false
                                    // }
                                    type="text"
                                    maxLength={10}
                                    name="referrer_contact"
                                    value={form?.referrer_contact}
                                    placeholder={strings.referrerNumber}
                                    onChange={handleInputField}
                                    autoComplete="off"
                                    onKeyPress={(event) => {
                                      if (/^[a-z]+$/.test(event.key)) {
                                        event.preventDefault();
                                      }
                                    }}
                                    onFocus={() => setVisitResponse({})}
                                  />
                                  {formVaild?.referrer_contact && (
                                    <span style={{ color: "red" }}>
                                      {formVaild?.referrer_contact}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Display referrer name and email fields */}
                            <div className="row">
                              <div className="col-md-8">
                                <div className="mb-3">
                                  <label htmlFor="referrerName">
                                    {strings.referrerName}
                                  </label>
                                  <input
                                    className="form-control"
                                    id="referrerName"
                                    type="text"
                                    name="referrer_name"
                                    value={form.referrer_name}
                                    placeholder={strings.referrerName}
                                    onChange={handleInputField}
                                    autoComplete="off"
                                  />
                                  {formVaild?.referrer_name && (
                                    <span style={{ color: "red" }}>
                                      {formVaild?.referrer_name}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="row">
                              <div className="col-md-8">
                                <div className="mb-3">
                                  <label htmlFor="referrerEmail">
                                    {strings.referrerEmail}
                                  </label>
                                  <input
                                    className="form-control"
                                    id="referrerEmail"
                                    type="email"
                                    name="referrer_email"
                                    value={form.referrer_email}
                                    placeholder={strings.referrerEmail}
                                    onChange={handleInputField}
                                    autoComplete="off"
                                  />
                                  {formVaild?.referrer_email && (
                                    <span style={{ color: "red" }}>
                                      {formVaild?.referrer_email}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </>
                        ) : null}
                        </>
                        ) : null}
                      </div>
                    ) : (
                      <div>
                        <div className="row">
                          <div className="col-md-8">
                            <div className="mb-3">
                              <label htmlFor="exampleFormControlLastName">
                                {strings.leadSource}
                                {isGREUser && (
                                  <span className="gre-sm-lead-hint">
                                    {" "}
                                    {strings.leadForSmGreHint}
                                  </span>
                                )}
                                <span className="required-field"></span>
                              </label>
                              <select
                                  className="form-control digits"
                                  id="formcontrol-select1"
                                  name="lead_source"
                                  value={form.lead_source}
                                  onChange={handleInputField}
                                  disabled={
                                    leadId &&
                                    !edit &&
                                    !form.isSousingManagerLead
                                  }
                                >
                                  <option value="">
                                    {strings.selectLeadSource}
                                  </option>
                                  {leadSourceType &&
                                    leadSourceType.map((item, index) => (
                                      <option key={index} value={item._id}>
                                        {item.title}
                                      </option>
                                    ))}
                                </select>
                              {formVaild?.lead_source && (
                                <span style={{ color: "red" }}>
                                  {formVaild?.lead_source}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* JUSTO-64: CP lead type (Walk-in / Tele-calling) as radio buttons in alternate form layout. */}
                        {form.cp_type === "2" &&
                        (mixTeam ||
                          SMteam ||
                          form.lead_source === CONST_IDS?.cp_lead_source_id ||
                          form.lead_source === CONST_IDS?.ref_lead_source_id) &&
                        form.lead_source === CONST_IDS?.cp_lead_source_id ? (
                          <>
                            <div className="row">
                              <div className="col-md-8">
                                <div className="mb-3">
                                  <label htmlFor="cpLeadType">
                                    {strings.cpLeadType}
                                    <span className="required-field"></span>
                                  </label>
                                  <div
                                    className="d-flex flex-wrap"
                                    style={{ gap: "1rem" }}
                                  >
                                    <label
                                      className="d-flex align-items-center cursor-pointer"
                                      style={{ gap: "0.5rem" }}
                                    >
                                      <input
                                        type="radio"
                                        name="cp_lead_type"
                                        value="1"
                                        checked={form.cp_lead_type === "1"}
                                        onChange={handleLeadTypeChange}
                                      />
                                      {strings.walk_in}
                                    </label>
                                    <label
                                      className="d-flex align-items-center cursor-pointer"
                                      style={{ gap: "0.5rem" }}
                                    >
                                      <input
                                        type="radio"
                                        name="cp_lead_type"
                                        value="2"
                                        checked={form.cp_lead_type === "2"}
                                        onChange={handleLeadTypeChange}
                                      />
                                      {strings.teleCalling}
                                    </label>
                                  </div>
                                  {formVaild?.cp_lead_type && (
                                    <span style={{ color: "red" }}>
                                      {formVaild?.cp_lead_type}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-md-8">
                                <div className="mb-3">
                                  <label htmlFor="exampleFormControlLastName">
                                    {strings.cpCompany}
                                    <span className="required-field"></span>
                                  </label>
                                  <select
                                    className="form-control digits"
                                    id="formcontrol-select1"
                                    name="cp_id"
                                    value={form.cp_id}
                                    onChange={handleInputField3}
                                    // onFocus={() => handleEmployeeDropdownPress()}
                                    onBlur={() => callEmploy()}
                                    // isSearchable={true}
                                  >
                                    <option value="">
                                      {strings.selectCpCompany}
                                    </option>
                                    {companyList &&
                                      companyList.map((item, index) => (
                                        <option key={index} value={item._id}>
                                          {item.agency_name}
                                        </option>
                                      ))}
                                  </select>
                                  {formVaild?.cp_id && (
                                    <span style={{ color: "red" }}>
                                      {formVaild?.cp_id}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            {form?.cp_id !== "" ||
                            ((mixTeam || SMteam) &&
                              form.lead_source ===
                                CONST_IDS?.cp_lead_source_id) ? (
                              <div className="row">
                                <div className="col-md-8">
                                  <div className="mb-3">
                                    <label htmlFor="exampleFormControlLastName">
                                      {strings.companyEmployName}
                                      {/* <span className="required-field"></span> */}
                                    </label>
                                    <select
                                      className="form-control digits"
                                      id="formcontrol-select1"
                                      name="cp_emp_id"
                                      value={form.cp_emp_id}
                                      onChange={handleInputField}
                                      onFocus={() =>
                                        handleEmployeeDropdownPress()
                                      }
                                    >
                                      <option value="">
                                        {strings.selectEmployeeName}
                                      </option>
                                      {employeeList &&
                                        employeeList.map((item, index) => (
                                          <option
                                            key={index}
                                            value={item.login_id}
                                          >
                                            {item.employee_name}
                                          </option>
                                        ))}
                                    </select>
                                    {formVaild?.cp_emp_id && (
                                      <span style={{ color: "red" }}>
                                        {formVaild?.cp_emp_id}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ) : null}
                          </>
                        ) : null}

                        {form.lead_source === CONST_IDS?.ref_lead_source_id ||
                        form.lead_source === referralPartnerMaster ? (
                          <>
                            <div className="row">
                              <div className="col-md-8">
                                <div className="mb-3">
                                  <label htmlFor="exampleInputMobileNo">
                                    {strings.referrerNumber}
                                    <span className="required-field"></span>
                                  </label>
                                  <input
                                    className="form-control"
                                    id="exampleInputMobileNo"
                                    // readOnly={
                                    //   isCreated
                                    //     ? false
                                    //     : form?.lead_id
                                    //     ? true
                                    //     : false
                                    // }
                                    type="text"
                                    maxLength={10}
                                    name="referrer_contact"
                                    value={
                                      // leadId
                                      //   ? isCreated
                                      //     ? form?.referrer_contact
                                      //     : form?.referrer_contact
                                      //     ? hideMobileNumber(
                                      //         form?.referrer_contact
                                      //       )
                                      //     : ""
                                      //   :
                                      form?.referrer_contact
                                    }
                                    placeholder={strings.referrerNumber}
                                    onChange={handleInputField}
                                    autoComplete="off"
                                    onKeyPress={(event) => {
                                      if (/^[a-z]+$/.test(event.key)) {
                                        event.preventDefault();
                                      }
                                    }}
                                    onFocus={() => setVisitResponse({})}
                                  />
                                  {formVaild?.referrer_contact && (
                                    <span style={{ color: "red" }}>
                                      {formVaild?.referrer_contact}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Display referrer name and email fields */}
                            <div className="row">
                              <div className="col-md-8">
                                <div className="mb-3">
                                  <label htmlFor="referrerName">
                                    {strings.referrerName}
                                  </label>
                                  <input
                                    className="form-control"
                                    id="referrerName"
                                    type="text"
                                    name="referrer_name"
                                    value={form.referrer_name}
                                    placeholder={strings.referrerName}
                                    onChange={handleInputField}
                                    autoComplete="off"
                                  />
                                  {formVaild?.referrer_name && (
                                    <span style={{ color: "red" }}>
                                      {formVaild?.referrer_name}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="row">
                              <div className="col-md-8">
                                <div className="mb-3">
                                  <label htmlFor="referrerEmail">
                                    {strings.referrerEmail}
                                  </label>
                                  <input
                                    className="form-control"
                                    id="referrerEmail"
                                    type="email"
                                    name="referrer_email"
                                    value={form.referrer_email}
                                    placeholder={strings.referrerEmail}
                                    onChange={handleInputField}
                                    autoComplete="off"
                                  />
                                  {formVaild?.referrer_email && (
                                    <span style={{ color: "red" }}>
                                      {formVaild?.referrer_email}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </>
                        ) : null}

                        {leadId ? (
                          <div className="row ">
                            <div className="col-md-8 ">
                              <div className="mb-3">
                                <label htmlFor="exampleFormControlFirstName">
                                  {strings.propertyname}
                                  <span className="required-field"></span>
                                </label>
                                <input
                                  className="form-control"
                                  id="exampleFormControlFirstName"
                                  type="text"
                                  name="property_title"
                                  value={form.property_title}
                                  readOnly
                                  placeholder={strings.propertyname}
                                  onChange={handleInputField}
                                />
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="row ">
                            <div className="col-md-8 ">
                              <div className="mb-3">
                                <label htmlFor="exampleFormControlFirstName">
                                  {strings.propertyname}
                                  <span className="required-field"></span>
                                </label>
                                <select
                                  className="form-control digits"
                                  id="formcontrol-select1"
                                  name="property_id"
                                  value={form.property_id}
                                  onFocus={() =>
                                    form?.mobile?.length === 10
                                      ? null
                                      : errorToast(strings.mobileErr)
                                  }
                                  onChange={(e) => {
                                    handlePropertySelectChange(e.target.value);
                                  }}
                                >
                                  <option defaultValue={null}>
                                    {strings.selectProp}
                                  </option>
                                  {form?.mobile?.length === 10
                                    ? Cmteam ||
                                      ((mixTeam || SMteam) &&
                                        form.lead_source !==
                                          CONST_IDS?.cp_lead_source_id)
                                      ? sourcingPropertyList.map(
                                          (item, index) => (
                                            <option
                                              key={index}
                                              value={getPropertyOptionValue(item)}
                                            >
                                              {item.property_title}
                                            </option>
                                          ),
                                        )
                                      : allProperty &&
                                        allProperty.map((item, index) => (
                                          <option
                                            key={index}
                                            value={getPropertyOptionValue(item)}
                                          >
                                            {item.property_title}
                                          </option>
                                        ))
                                    : emptyArray.map((item, index) => (
                                        <option
                                          key={index}
                                          value={getPropertyOptionValue(item)}
                                        >
                                          {item.property_title}
                                        </option>
                                      ))}
                                </select>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="row">
                      <div className="col-12 col-md-8">
                        <div
                          className={`mb-3${isGreSinglePageAdd ? " gre-choice-field" : ""}`}
                        >
                          <label htmlFor="gender">
                            {strings.gender}{" "}
                            <span className="required-field"></span>
                          </label>
                          <div
                            className={
                              isGreSinglePageAdd
                                ? "gre-choice-group"
                                : "row"
                            }
                          >
                            {/* Male */}
                            {isGreSinglePageAdd ? (
                              <div className="form-check gre-choice-chip">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="gender"
                                  id="radio_male"
                                  value={1}
                                  checked={Number(form.gender) === 1}
                                  onChange={handleInputField}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="radio_male"
                                >
                                  {strings.male}
                                </label>
                              </div>
                            ) : (
                              <div className="col-6 col-sm-6 col-md-3 mb-2">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="gender"
                                    id="radio_male"
                                    value={1}
                                    checked={Number(form.gender) === 1}
                                    onChange={handleInputField}
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="radio_male"
                                  >
                                    {strings.male}
                                  </label>
                                </div>
                              </div>
                            )}
                            {/* Female */}
                            {isGreSinglePageAdd ? (
                              <div className="form-check gre-choice-chip">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="gender"
                                  id="radio_female"
                                  value={2}
                                  checked={Number(form.gender) === 2}
                                  onChange={handleInputField}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="radio_female"
                                >
                                  {strings.female}
                                </label>
                              </div>
                            ) : (
                              <div className="col-6 col-sm-6 col-md-3 mb-2">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="gender"
                                    id="radio_female"
                                    value={2}
                                    checked={Number(form.gender) === 2}
                                    onChange={handleInputField}
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="radio_female"
                                  >
                                    {strings.female}
                                  </label>
                                </div>
                              </div>
                            )}
                          </div>
                          {formVaild?.gender && (
                            <span style={{ color: "red" }}>
                              {formVaild?.gender}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-8">
                        <div className="mb-3">
                          <label htmlFor="annualHouseholdIncomeSelect">
                            {strings.annualHouseholdIncome}
                          </label>
                          <select
                            id="annualHouseholdIncomeSelect"
                            name="income"
                            className="form-control"
                            value={form.income || ""}
                            onChange={handleInputField}
                          >
                            <option value="">
                              {strings.selectAnnualHouseholdIncome}
                            </option>
                            {ANNUAL_HOUSEHOLD_INCOME_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          {formVaild?.income && (
                            <span style={{ color: "red" }}>
                              {formVaild.income}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-8">
                        <div className="mb-3">
                          <label htmlFor="exampleInputMobileNo">
                            {strings.whatsappNo}
                          </label>
                          <input
                            className="form-control"
                            id="exampleInputMobileNo"
                            type="text"
                            maxLength={10}
                            name="whatsapp_no"
                            value={form.whatsapp_no}
                            placeholder={strings.whatsappNo}
                            onChange={handleInputField}
                            autoComplete="off"
                            onKeyPress={(event) => {
                              if (/^[a-z]+$/.test(event.key)) {
                                event.preventDefault();
                              }
                            }}
                          />
                          {formVaild?.whatsapp_no && (
                            <span style={{ color: "red" }}>
                              {formVaild?.whatsapp_no}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="row pt-2">
                      <div className="col-md-8">
                        <div className="mb-3">
                          <label htmlFor="exampleInputMobileNo">
                            {strings.emailAddress}
                            {isMdocEmailRequired(
                              form.property_id,
                              form.property_title ||
                                greDefaultProperty?.property_title,
                            ) ? (
                              <span className="required-field"></span>
                            ) : null}
                          </label>
                          <input
                            className="form-control"
                            id="exampleInputMobileNo"
                            autoComplete="off"
                            // readOnly={form.lead_id ? true : false}
                            type="email"
                            name="email"
                            value={form.email}
                            placeholder={strings.emailaddress}
                            onChange={handleInputField}
                          />
                          {formVaild?.email && (
                            <span style={{ color: "red" }}>
                              {formVaild?.email}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    </div>
                    <div
                      className={
                        isJustoSinglePage ? "justo-visitor-form__column" : undefined
                      }
                    >
                      {isJustoSinglePage ? (
                        <h2 className="justo-visitor-form__section-title">
                          Lead And Preference Details
                        </h2>
                      ) : null}

                    <div className="row pt-2">
                      <div className="col-md-8">
                        <div className="mb-3">
                          <label htmlFor="exampleInputMobileNo">
                            {strings.residentLocation}
                          </label>
                          <input
                            className="form-control"
                            id="exampleInputMobileNo"
                            placeholder={strings.location}
                            value={form.location}
                            onChange={handleInputField}
                            // onSelect={onSelect}
                            name="location"
                            type="text"
                            autoComplete="off"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row pt-2">
                      <div className="col-md-8">
                        <div className="mb-3">
                          <label htmlFor="exampleFormControlInput1">
                            {strings.workLocation}
                          </label>
                          <input
                            className="form-control"
                            id="exampleFormControlInput1"
                            type="text"
                            name="office_address"
                            value={form.office_address}
                            placeholder={strings.workLocation}
                            onChange={handleInputField}
                            autoComplete="off"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-8">
                        {leadId ? (
                          <div className="mb-3">
                            <label htmlFor="exampleFormControlLastName">
                              {strings.ethniCity}
                            </label>
                            <select
                              className="form-control digits"
                              id="formcontrol-select1"
                              name="ethnicity_id"
                              value={form.ethnicity_id}
                              onChange={(e) => {
                                var index = e.target.selectedIndex;
                                var ethnicity = e.target[index].text;
                                const nextFormState = {
                                  ...form,
                                  ethnicity_id: e.target.value,
                                  ethnicity: ethnicity,
                                };
                                setForm(nextFormState);
                              }}
                            >
                              {form.ethnicity_id ? (
                                <></>
                              ) : (
                                <option value={""}>
                                  {strings.selectEthnicity}
                                </option>
                              )}

                              {ethnicityList.length > 0 ? (
                                ethnicityList.map((item, i) => (
                                  <option key={i} value={item._id}>
                                    {item.title}
                                  </option>
                                ))
                              ) : (
                                <option></option>
                              )}
                            </select>
                          </div>
                        ) : (
                          <div className="mb-3">
                            <label htmlFor="exampleFormControlLastName">
                              {strings.ethniCity}
                            </label>
                            <select
                              className="form-control digits"
                              id="formcontrol-select1"
                              name="ethnicity_id"
                              value={form.ethnicity_id}
                              onChange={(e) => {
                                var index = e.target.selectedIndex;
                                var ethnicity = e.target[index].text;
                                const nextFormState = {
                                  ...form,
                                  ethnicity_id: e.target.value,
                                  ethnicity: ethnicity,
                                };
                                setForm(nextFormState);
                              }}
                            >
                              <option value={""}>
                                {strings.selectEthnicity}
                              </option>
                              {ethnicityList.length > 0 ? (
                                ethnicityList.map((item, i) => (
                                  <option key={i} value={item._id}>
                                    {item.title}
                                  </option>
                                ))
                              ) : (
                                <option></option>
                              )}
                            </select>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="row ">
                      <div className="col-md-8 ">
                        <div className="mb-3">
                          <label htmlFor="exampleFormControlFirstName">
                            {strings.maritalStatus}
                          </label>
                          <select
                            className="form-control digits"
                            id="formcontrol-select1"
                            name="marital_status"
                            value={form.marital_status}
                            onChange={handleInputField}
                          >
                            <option defaultValue={null}>
                              {strings.selectMaritalStatus}
                            </option>
                            <option value={2}>{strings.married}</option>
                            <option value={1}>{strings.unmarried}</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-12 col-md-8">
                        <div className="mb-3">
                          <label htmlFor="visit_with">
                            {strings.visitingWith}
                          </label>
                          <div className="row">
                            {/* Family */}
                            <div className="col-6 col-sm-6 col-md-3 mb-2">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="visit_with"
                                  id="radio_family"
                                  value="family"
                                  checked={form.visit_with === "family"}
                                  onChange={handleInputField}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="radio_family"
                                >
                                  {strings.family}
                                </label>
                              </div>
                            </div>

                            {/* Couple */}
                            <div className="col-6 col-sm-6 col-md-3 mb-2">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="visit_with"
                                  id="radio_couple"
                                  value="couple"
                                  checked={form.visit_with === "couple"}
                                  onChange={handleInputField}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="radio_couple"
                                >
                                  {strings.couple}
                                </label>
                              </div>
                            </div>

                            {/* Friend */}
                            <div className="col-6 col-sm-6 col-md-3 mb-2">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="visit_with"
                                  id="radio_friend"
                                  value="friend"
                                  checked={form.visit_with === "friend"}
                                  onChange={handleInputField}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="radio_friend"
                                >
                                  {strings.friend}
                                </label>
                              </div>
                            </div>

                            {/* Single */}
                            <div className="col-6 col-sm-6 col-md-3 mb-2">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="visit_with"
                                  id="radio_single"
                                  value="single"
                                  checked={form.visit_with === "single"}
                                  onChange={handleInputField}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="radio_single"
                                >
                                  {strings.single}
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row ">
                      <div className="col-md-8 ">
                        <div className="mb-3">
                          <label htmlFor="exampleFormControlFirstName">
                            {strings.currentStay}
                          </label>
                          <select
                            className="form-control digits"
                            id="formcontrol-select1"
                            name="current_stay"
                            value={form.current_stay}
                            onChange={handleInputField}
                          >
                            <option defaultValue={null}>
                              {strings.currentStayselect}
                            </option>
                            <option value={"Rented"}>{strings.rented}</option>
                            <option value={"Owned"}>{strings.owned}</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-8">
                        <div className="mb-3">
                          <label htmlFor="occupationSelect">
                            {strings.occupation}
                          </label>
                          <select
                            id="occupationSelect"
                            name="occupation"
                            className="form-control"
                            value={form.occupation}
                            onChange={handleInputField}
                          >
                            <option value="">-- Select Occupation --</option>
                            <option value="salaried">{strings.salaried}</option>
                            <option value="business">{strings.business}</option>
                            <option value="professional">
                              {strings.professional}
                            </option>
                            <option value="retired">{strings.retired}</option>
                            <option value="home maker">
                              {strings.homeMaker}
                            </option>
                            <option value="self employee">
                              {strings.selfEmployed}
                            </option>
                            <option value="others">{strings.others}</option>
                          </select>
                        </div>
                      </div>
                    </div>

                  {isSinglePageForm && (
                    <div
                      className={
                        isGreSinglePageAdd
                          ? "gre-visitor-form-panel gre-visitor-form-panel--preferences"
                          : "justo-visitor-form__preferences"
                      }
                    >
                      {isGreSinglePageAdd ? (
                        <GreVisitorFormSectionHeader
                          step="2"
                          title={strings.propertyRequired}
                        />
                      ) : (
                        <h2 className="justo-visitor-form__section-title justo-visitor-form__section-title--spaced">
                          {strings.propertyRequired}
                        </h2>
                      )}
                      <div className="row">
                        <div className="col-md-8">
                          <div className="mb-3">
                            <label htmlFor="configuration_id">
                              {strings.configuration}
                            </label>
                            <select
                              className="form-control digits"
                              id="configuration_id"
                              name="configuration_id"
                              value={form.configuration_id || ""}
                              onChange={(e) => {
                                const index = e.target.selectedIndex;
                                const configuration = e.target[index].text;
                                setForm({
                                  ...form,
                                  configuration_id: e.target.value,
                                  configuration: configuration,
                                });
                              }}
                            >
                              <option value="">
                                {strings.selectConfiguration}
                              </option>
                              {configurationsType.length > 0 ? (
                                configurationsType.map((item, i) => (
                                  <option key={i} value={item._id}>
                                    {item.title}
                                  </option>
                                ))
                              ) : (
                                <option></option>
                              )}
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-8">
                          <div className="mb-3">
                            <label htmlFor="expected_possession_periodSelect">
                              {strings.expectedPossessionPeriode}
                            </label>
                            <select
                              id="expected_possession_periodSelect"
                              name="expected_possession_period"
                              className="form-control"
                              value={form.expected_possession_period || ""}
                              onChange={handleInputField}
                            >
                              <option value="">
                                {strings.selectExpectedPossession}
                              </option>
                              <option value="3 – 6 Months">
                                {strings.three_to_six_month}
                              </option>
                              <option value="6 Months – 1 Year">
                                {strings.six_to_one_year}
                              </option>
                              <option value="1 – 2 Years">
                                {strings.one_year_to_two_year}
                              </option>
                              <option value="2 Years and Above">
                                {strings.two_year_and_above}
                              </option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-8">
                          <div className="mb-3">
                            <label htmlFor="budget_amountSelect">
                              {strings.budgetAmount}
                            </label>
                            <select
                              id="budget_amountSelect"
                              name="budget_amount"
                              className="form-control"
                              value={form.budget_amount || ""}
                              onChange={handleInputField}
                            >
                              <option value="">
                                {strings.selectBudgetAmount}
                              </option>
                              <option value="Below - 50L">
                                {strings.below_50_lakhs}
                              </option>
                              <option value="50L - 75L">
                                {strings.fifty_lakhs_to_seventy_five_lakhs}
                              </option>
                              <option value="75L - 1Cr">
                                {strings.seventy_five_lakhs_to_one_crore}
                              </option>
                              <option value="1Cr - 1.30Cr">
                                {strings.one_crore_to_1_30_crore}
                              </option>
                              <option value="1.31Cr - 1.50Cr">
                                {strings.one_31_crore_to_1_50_crore}
                              </option>
                              <option value="1.51Cr - 1.75Cr">
                                {strings.one_51_crore_to_1_75_crore}
                              </option>
                              <option value="1.76Cr - 2Cr">
                                {strings.one_76_crore_to_2_crore}
                              </option>
                              <option value="2Cr - 2.5Cr">
                                {strings.two_crore_to_2_5_crore}
                              </option>
                              <option value="2.5Cr and above">
                                {strings.above_2_5_crore}
                              </option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-8">
                          <div className="mb-3">
                            <label htmlFor="areain_sqlft">{strings.Area}</label>
                            <input
                              className="form-control"
                              id="areain_sqlft"
                              type="number"
                              name="areain_sqlft"
                              value={form.areain_sqlft || ""}
                              onChange={handleInputField}
                              placeholder={strings.Area}
                              autoComplete="off"
                              onKeyPress={(e) => {
                                if (e.target.value.length >= 10) {
                                  e.preventDefault();
                                }
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="row pt-2">
                        <div className="col-md-8">
                          <div className="mb-3 gre-choice-field">
                            <label htmlFor="funding_type">
                              {strings.natureOffunding}
                            </label>
                            <div className="gre-choice-group custom-radio-ml">
                                <div className="radio radio-primary gre-choice-chip">
                                  <input
                                    id="radioinline1"
                                    type="radio"
                                    name="funding_type"
                                    value="loan"
                                    checked={form.funding_type === "loan"}
                                    onChange={handleInputField}
                                  />
                                  <label
                                    className="mb-0"
                                    htmlFor="radioinline1"
                                  >
                                    {strings.loan}
                                  </label>
                                </div>
                                <div className="radio radio-primary gre-choice-chip">
                                  <input
                                    id="radioinline2"
                                    type="radio"
                                    name="funding_type"
                                    value="self"
                                    checked={form.funding_type === "self"}
                                    onChange={handleInputField}
                                  />
                                  <label
                                    className="mb-0"
                                    htmlFor="radioinline2"
                                  >
                                    {strings.selfFunding}
                                  </label>
                                </div>
                                <div className="radio radio-primary gre-choice-chip">
                                  <input
                                    id="radioinline3"
                                    type="radio"
                                    name="funding_type"
                                    value="both"
                                    checked={form.funding_type === "both"}
                                    onChange={handleInputField}
                                  />
                                  <label
                                    className="mb-0"
                                    htmlFor="radioinline3"
                                  >
                                    {strings.both}
                                  </label>
                                </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="row pt-3">
                        <div className="col-md-8">
                          <div className="mb-3 gre-choice-field">
                            <label htmlFor="purpose">{strings.Purpose}</label>
                            <div className="gre-choice-group custom-radio-ml">
                                <div className="radio radio-primary gre-choice-chip">
                                  <input
                                    id="radioinline4"
                                    type="radio"
                                    name="purpose"
                                    value="end user"
                                    checked={form.purpose === "end user"}
                                    onChange={handleInputField}
                                  />
                                  <label
                                    className="mb-0"
                                    htmlFor="radioinline4"
                                  >
                                    {strings.endUser}
                                  </label>
                                </div>
                                <div className="radio radio-primary gre-choice-chip">
                                  <input
                                    id="radioinline5"
                                    type="radio"
                                    name="purpose"
                                    value="invest"
                                    checked={form.purpose === "invest"}
                                    onChange={handleInputField}
                                  />
                                  <label
                                    className="mb-0"
                                    htmlFor="radioinline5"
                                  >
                                    {strings.invesment}
                                  </label>
                                </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {!hideGreLeadStatusAndPriority && (
                    <>
                      <div className="row">
                        <div className="col-12 col-md-8">
                          <div className="mb-3">
                            <label htmlFor="qualified">
                              {strings.leadStatus}
                            </label>
                            <div className="row">
                              {/* Qualified (True) */}
                              <div className="col-6 col-sm-6 col-md-3 mb-2">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="qualified"
                                    id="radio_qualified_true"
                                    value="true"
                                    checked={form.qualified === true}
                                    onChange={handleInputField}
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="radio_qualified_true"
                                  >
                                    {strings.qualified}
                                  </label>
                                </div>
                              </div>

                              {/* Unqualified (False) */}
                              <div className="col-6 col-sm-6 col-md-3 mb-2">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="qualified"
                                    id="radio_qualified_false"
                                    value="false"
                                    checked={form.qualified === false}
                                    onChange={handleInputField}
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="radio_qualified_false"
                                  >
                                    {strings.unQualified}
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-12 col-md-8">
                          <div className="mb-3">
                            <label htmlFor="visit_with">
                              {strings.leadPriority}
                            </label>
                            <div className="row">
                              {/* hot */}
                              <div className="col-6 col-sm-6 col-md-3 mb-2">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="lead_priority"
                                    id="radio_hot"
                                    value="hot"
                                    checked={form.lead_priority === "hot"}
                                    onChange={handleInputField}
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="radio_hot"
                                  >
                                    {strings.hot}
                                  </label>
                                </div>
                              </div>

                              {/* warm */}
                              <div className="col-6 col-sm-6 col-md-3 mb-2">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="lead_priority"
                                    id="radio_warm"
                                    value="warm"
                                    checked={form.lead_priority === "warm"}
                                    onChange={handleInputField}
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="radio_warm"
                                  >
                                    {strings.warm}
                                  </label>
                                </div>
                              </div>

                              {/* cold */}
                              <div className="col-6 col-sm-6 col-md-3 mb-2">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="lead_priority"
                                    id="radio_cold"
                                    value="cold"
                                    checked={form.lead_priority === "cold"}
                                    onChange={handleInputField}
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="radio_cold"
                                  >
                                    {strings.cold}
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {showWalkinRemarkSection ? (
                    <div className="justo-visitor-form__section justo-visitor-form__walkin">
                      <h2 className="justo-visitor-form__section-title justo-visitor-form__section-title--spaced">
                        {strings.walkinRemark}
                      </h2>
                      <div className="justo-visitor-form__walkin-fields">
                        <div className="justo-visitor-form__field justo-visitor-form__field--full">
                          <label
                            className="justo-visitor-form__label"
                            htmlFor="walkin_remark"
                          >
                            {strings.walkinRemark}
                            <span className="required-field" />
                          </label>
                          <textarea
                            className={`justo-visitor-form__textarea${
                              formVaild?.walkin_remark ? " is-invalid" : ""
                            }`}
                            id="walkin_remark"
                            name="walkin_remark"
                            rows={3}
                            value={form.walkin_remark || ""}
                            placeholder={
                              strings.walkinRemarkPlaceholder ||
                              strings.walkinRemark
                            }
                            onChange={handleInputField}
                            autoComplete="off"
                          />
                          {formVaild?.walkin_remark ? (
                            <span className="justo-visitor-form__field-error">
                              {formVaild.walkin_remark}
                            </span>
                          ) : null}
                        </div>
                        <div className="justo-visitor-form__field justo-visitor-form__field--full">
                          <AppointmentScheduleFields
                            dateId="followup_date"
                            hourId="followup_hour"
                            selectedDate={
                              form.followup_date
                                ? moment(
                                    form.followup_date,
                                    "YYYY-MM-DD",
                                  ).toDate()
                                : null
                            }
                            onDateChange={handleWalkinFollowupDate}
                            appointmentTime={form.followup_time || ""}
                            onTimeChange={handleWalkinFollowupTime}
                            dateError={formVaild?.followup_date}
                            timeError={formVaild?.followup_time}
                            minDate={moment().startOf("day").toDate()}
                            dateLabel={strings.addVisitorFollowupDate}
                            timeLabel={strings.addVisitorFollowupTime}
                            datePlaceholder={strings.selectAppointmentDate}
                            showTimeHint={false}
                            required
                          />
                        </div>
                      </div>
                    </div>
                  ) : null}
                    </div>
                  </div>
                </div>
                {isGreSinglePageAdd ? (
                  <div className="gre-visitor-form-footer">
                    <button
                      className="btn btn-color"
                      type="button"
                      onClick={() => handleNext()}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <i
                            className="fa fa-spinner fa-spin"
                            aria-hidden="true"
                          />{" "}
                          Processing...
                        </>
                      ) : (
                        strings.createVisitor
                      )}
                    </button>
                    {!isEmpty(formVaild) && (
                      <span className="gre-visitor-form-field-error">
                        {strings.fieldRequired}
                      </span>
                    )}
                  </div>
                ) : isJustoSinglePage ? (
                  <div className="justo-visitor-form__footer">
                    <button
                      className="justo-visitor__btn justo-visitor__btn--outline"
                      type="button"
                      onClick={() => navigatpage("/visitor")}
                      disabled={isLoading}
                    >
                      {strings.cancel}
                    </button>
                    {!isClosingTeamOnly && isSinglePageAdd ? (
                      <button
                        className="justo-visitor__btn justo-visitor__btn--outline"
                        type="button"
                        onClick={() => {
                          if (!handleValid()) {
                            handleStandardCreate({ schedule: true });
                          }
                        }}
                        disabled={isLoading}
                      >
                        {isLoading
                          ? "Processing..."
                          : strings.createAndSchedule}
                      </button>
                    ) : null}
                    <button
                      className="justo-visitor__btn justo-visitor__btn--solid"
                      type="button"
                      onClick={() => handleNext()}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <i
                            className="fa fa-spinner fa-spin"
                            aria-hidden="true"
                          />{" "}
                          Processing...
                        </>
                      ) : isSinglePageEdit ? (
                        strings.update
                      ) : (
                        strings.createVisitor
                      )}
                    </button>
                    {!isEmpty(formVaild) && (
                      <span className="justo-visitor-form__error">
                        {strings.fieldRequired}
                      </span>
                    )}
                  </div>
                ) : null}
              </form>
            </div>
          </div>
        </div>
      </div>
      {visitModule && <div className="modal-backdrop fade show"></div>}

      {isGreSinglePageAdd &&
        showAllocateModal &&
        createdLeadId &&
        createdPropertyId && (
          <AllocateSM
            show={showAllocateModal}
            propertyId={createdPropertyId}
            leadId={createdLeadId}
            handleClose={() => {
              setShowAllocateModal(false);
              setCreatedLeadId(null);
              setCreatedPropertyId(null);
              setTimeout(() => {
                navigatpage("/visitor");
              }, 100);
            }}
            onAllocateSuccess={() => {
              setShowAllocateModal(false);
              setCreatedLeadId(null);
              setCreatedPropertyId(null);
              setTimeout(() => {
                navigatpage("/visitor");
              }, 100);
            }}
          />
        )}
    </div>
  );
};
export default EditVisitorNext;
